<template>
  <div class="page-container">
    <header class="header">
      <button @click="handleBack" class="back-btn" aria-label="返回">
        <img src="@/assets/返回.svg" alt="返回" class="icon-back" />
      </button>
      <h1 class="title">出库单打印</h1>
      <div class="header-right">
        <button
          @click="openDeliveryPlanImport"
          class="link-btn"
          aria-label="送货计划单文件导入"
          title="送货计划单文件导入"
        >
          <el-icon class="link-btn-icon"><Upload /></el-icon>
          文件导入
        </button>
      </div>
    </header>

    <main class="content">
      <div class="search-panel">
        <el-form
          class="filter-form"
          label-width="80px"
          @submit.prevent="handleSearch"
        >
          <el-form-item label="供应商">
            <el-autocomplete
              v-model="supplierKeyword"
              placeholder="请输入供应商名称"
              :fetch-suggestions="fetchSupplierSuggestions"
              clearable
              style="width: 100%"
              @select="selectSupplier"
              @clear="clearSelectedSupplier"
              @keyup.enter="handleSupplierSearch"
            />
          </el-form-item>

          <el-form-item label="送货单号">
            <el-input-tag
              v-model="resourceNoTags"
              clearable
              trigger="Enter"
              :delimiter="/[\n,，\s]+/"
              placeholder="输入后回车确认，可粘贴多个"
              style="width: 100%"
            />
          </el-form-item>

          <el-form-item label="子库">
            <el-input-tag
              v-model="invCodeTags"
              clearable
              trigger="Enter"
              :delimiter="/[\n,，\s]+/"
              placeholder="输入后回车确认，可粘贴多个"
              style="width: 100%"
            />
          </el-form-item>

          <el-form-item label="物料编码">
            <el-input-tag
              v-model="materialNoTags"
              clearable
              trigger="Enter"
              :delimiter="/[\n,，\s]+/"
              placeholder="输入后回车确认，可粘贴多个"
              style="width: 100%"
            />
          </el-form-item>

          <el-form-item label="状态">
            <el-select v-model="wareOutStatus" clearable style="width: 100%">
              <el-option label="待分配" value="0" />
              <el-option label="待收货" value="1" />
              <el-option label="待确认" value="2" />
              <el-option label="已完成" value="3" />
            </el-select>
          </el-form-item>

          <el-form-item label="单据类型">
            <el-select v-model="wareOutType" style="width: 100%">
              <el-option label="送货出库" value="0" />
              <el-option label="退货出库" value="1" />
            </el-select>
          </el-form-item>

          <el-form-item label="是否打印">
            <el-select
              v-model="isPrint"
              style="width: 100%"
              @change="onPrintChange"
            >
              <el-option label="已打印" :value="1" />
              <el-option label="未打印" :value="0" />
            </el-select>
          </el-form-item>

          <el-form-item label="创建时间" class="form-item-date">
            <el-date-picker
              v-model="timeRange"
              type="datetimerange"
              unlink-panels
              range-separator="至"
              start-placeholder="开始时间"
              end-placeholder="结束时间"
              value-format="YYYY-MM-DDTHH:mm:ss"
              style="width: 100%"
              @change="handleTimeChange"
            />
          </el-form-item>

          <el-form-item class="form-item-actions" label-width="0">
            <el-button type="primary" @click="handleSearch">查询</el-button>
            <el-button @click="handleReset">重置</el-button>
          </el-form-item>
        </el-form>
      </div>

      <div v-loading="loading" class="table-panel">
        <div class="table-action-bar">
          <div class="selected-info">
            已选择
            <span class="selected-count">{{ selectedData.length }}</span> 项
            <span class="selected-qty">
              （出库数量：{{ selectedTotalQty.toFixed(2) }}）
            </span>
          </div>
          <div class="action-buttons">
            <el-button
              type="success"
              :disabled="selectedData.length === 0"
              @click="batchPrint"
            >
              PLS批量打印
            </el-button>
            <el-button
              type="primary"
              :disabled="selectedData.length === 0"
              @click="batchPrintNonPls"
            >
              非PLS批量打印
            </el-button>
            <el-button
              type="warning"
              :disabled="selectedData.length === 0"
              @click="batchPrintTclMonthly"
            >
              TCL月度标签打印
            </el-button>
            <el-button
              type="info"
              :disabled="selectedData.length === 0"
              @click="batchPrintA4"
            >
              A4批量打印
            </el-button>
          </div>
        </div>

        <el-table
          ref="tableRef"
          :data="list"
          :row-key="getRowKey"
          :row-class-name="getRowClassName"
          :span-method="calculateSpanMethod"
          height="calc(100vh - 280px)"
          border
          stripe
          highlight-current-row
          @selection-change="handleSelectionChange"
          class="print-data-table"
        >
          <el-table-column type="selection" width="48" fixed="left" />
          <el-table-column label="序号" type="index" width="56" fixed="left" />
          <el-table-column
            label="出库单号"
            min-width="160"
            show-overflow-tooltip
          >
            <template #default="{ row }">
              {{ row.wareOutNo || "" }}
            </template>
          </el-table-column>
          <el-table-column
            label="送货单号"
            min-width="160"
            show-overflow-tooltip
          >
            <template #default="{ row }">
              <span
                class="copyable"
                @dblclick="handleCopy(row.resourceNo, '送货单号')"
              >
                {{ row.resourceNo || "" }}
              </span>
            </template>
          </el-table-column>
          <el-table-column
            label="PLS单号"
            min-width="140"
            show-overflow-tooltip
          >
            <template #default="{ row }">
              {{ row.plsNo || "" }}
            </template>
          </el-table-column>
          <el-table-column
            label="供应商"
            min-width="120"
            show-overflow-tooltip
          >
            <template #default="{ row }">
              {{ row.supplierName || "" }}
            </template>
          </el-table-column>
          <el-table-column
            label="主机厂"
            min-width="120"
            show-overflow-tooltip
          >
            <template #default="{ row }">
              {{ row.factoryName || "" }}
            </template>
          </el-table-column>
          <el-table-column label="子库" width="90" show-overflow-tooltip>
            <template #default="{ row }">
              <span
                class="copyable"
                @dblclick="handleCopy(row.invCode, '子库编码')"
              >
                {{ row.invCode || "" }}
              </span>
            </template>
          </el-table-column>
          <el-table-column
            label="组合物料"
            min-width="140"
            show-overflow-tooltip
          >
            <template #default="{ row }">
              <span
                class="copyable"
                @dblclick="
                  handleCopy(row.composeMaterialNo || '', '组合物料编码')
                "
              >
                {{ row.composeMaterialNo || "" }}
              </span>
            </template>
          </el-table-column>
          <el-table-column
            label="物料编码"
            min-width="140"
            show-overflow-tooltip
          >
            <template #default="{ row }">
              <span
                class="copyable"
                @dblclick="handleCopy(row.materialNo || '', '物料编码')"
              >
                {{ row.materialNo || "" }}
              </span>
            </template>
          </el-table-column>
          <el-table-column label="出库数量" width="90" align="right">
            <template #default="{ row }">
              {{
                formatNumber(
                  parseFloat(row.demandNum) ||
                    parseFloat(row.wareOutNum) ||
                    parseFloat(row.prepareNum) ||
                    0,
                )
              }}
            </template>
          </el-table-column>
          <el-table-column label="是否打印" width="90" align="center">
            <template #default="{ row }">
              <el-tag
                :type="row.isPrint === 1 ? 'success' : 'info'"
                size="small"
              >
                {{ row.isPrint === 1 ? "已打印" : "未打印" }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="80" fixed="right" align="center">
            <template #default="{ row }">
              <el-button
                size="small"
                type="primary"
                link
                @click.stop="handleDetail(row.id)"
              >
                详情
              </el-button>
            </template>
          </el-table-column>
          <template #empty>
            <div class="empty-state">
              <img
                src="@/assets/无数据.svg"
                alt="暂无数据"
                class="empty-img"
              />
              <p class="empty-text">暂无数据</p>
            </div>
          </template>
        </el-table>

        <div class="pagination">
          <el-pagination
            @size-change="handleSizeChange"
            @current-change="handlePageChange"
            :current-page="current"
            :page-sizes="[20, 100, 300, 500, 999]"
            :page-size="size"
            :total="total"
            layout="total, sizes, prev, pager, next, jumper"
            background
          />
        </div>
      </div>
    </main>

    <el-dialog
      v-model="detailVisible"
      title="出库单详情"
      width="90%"
      :max-width="1200"
      :close-on-click-modal="false"
    >
      <div v-if="detailLoading" class="loading-state">
        <div class="loading-spinner"></div>
        <p class="loading-text">加载中...</p>
      </div>
      <div v-else-if="detailData" class="detail-content">
        <div class="detail-info-bar">
          <span class="info-item">
            <span class="info-label">出库单号：</span>
            <span class="info-value">{{ detailData.wareOutNo || "-" }}</span>
          </span>
          <span class="info-item">
            <span class="info-label">供应商：</span>
            <span class="info-value">{{ detailData.supplierName || "-" }}</span>
          </span>
          <span class="info-item">
            <span class="info-label">状态：</span>
            <span class="info-value">
              <el-tag :type="getStatusTagType(detailData.wareOutStatus)">
                {{ getStatusText(detailData.wareOutStatus) }}
              </el-tag>
            </span>
          </span>
          <span class="info-item">
            <span class="info-label">创建人：</span>
            <span class="info-value">{{ detailData.name || "-" }}</span>
          </span>
          <span class="info-item">
            <span class="info-label">创建时间：</span>
            <span class="info-value">{{ detailData.createTime || "-" }}</span>
          </span>
        </div>

        <div
          v-if="detailData.outDetailList && detailData.outDetailList.length > 0"
          class="detail-section"
        >
          <h3 class="detail-section-title">物料明细</h3>
          <div class="detail-table-wrapper">
            <el-table :data="detailData.outDetailList" border size="small">
              <el-table-column type="index" label="序号" width="60" />
              <el-table-column
                prop="composeMaterialNo"
                label="组合编码"
                width="160"
              />
              <el-table-column prop="materialNo" label="物料编码" width="160" />
              <el-table-column label="物料描述" min-width="200">
                <template #default="{ row }">
                  <span class="ellipsis-text" :title="row.materialDesc || ''">
                    {{ row.materialDesc || "-" }}
                  </span>
                </template>
              </el-table-column>
              <el-table-column prop="demandNum" label="需求数量" width="100" />
              <el-table-column prop="wareOutNum" label="出库数量" width="100" />
              <el-table-column prop="prepareNum" label="备货数量" width="100" />
              <el-table-column
                prop="wareKeeperName"
                label="仓管员"
                width="100"
              />
            </el-table>
          </div>
        </div>
      </div>
      <template #footer>
        <el-button type="primary" @click="detailVisible = false"
          >关闭</el-button
        >
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, nextTick } from "vue";
import { useRouter, useRoute } from "vue-router";
import QRCode from "qrcode";
import toast from "@/utils/toast";
import {
  getWareOutPrintPage,
  getResourceAndNoPlsNo,
  updateIsPrint,
  getWareOutOrderDetail,
} from "@/api/wareOut";
import { getSupplierPageGtZero } from "@/api/wareInReceiveCreate";
import { Upload } from "@element-plus/icons-vue";

export default {
  name: "WareOutPrintList",
  components: {
    Upload,
  },
  setup() {
    const router = useRouter();
    const route = useRoute();
    const current = ref(1);
    const size = ref(20);
    const list = ref([]);
    const total = ref(0);
    const loading = ref(false);

    const supplierKeyword = ref("");
    const supplierLoading = ref(false);
    const supplierDropdown = ref(false);
    const supplierList = ref([]);
    const selectedSupplier = ref(null);

    const resourceNoTags = ref([]);
    const invCodeTags = ref([]);
    const materialNoTags = ref([]);

    // 大整数 ID 一律按字符串处理，避免超过 Number.MAX_SAFE_INTEGER 丢精度
    const toIdStr = (v) => {
      if (v === null || v === undefined || v === "") return "";
      return String(v);
    };

    // 包装数量拆分：用整数换算避免 74.1/5.7 等浮点误差
    const formatPackagePack = (packageQty, demandQty, boxSuffix = "箱") => {
      const pkg = parseFloat(packageQty) || 0;
      const demand = parseFloat(demandQty) || 0;
      if (pkg <= 0 || demand <= 0) return "";
      const scale = 10000;
      const totalScaled = Math.round(demand * scale);
      const pkgScaled = Math.round(pkg * scale);
      const boxes = Math.floor(totalScaled / pkgScaled);
      const remainderScaled = totalScaled - boxes * pkgScaled;
      if (remainderScaled === 0) {
        return `${pkg} * ${boxes}${boxSuffix}`;
      }
      const remainder = parseFloat((remainderScaled / scale).toFixed(2));
      return `${pkg} * ${boxes}${boxSuffix} + ${remainder}只`;
    };

    // TCL月度标签包装格式：240 x 3 箱 + 8 尾数
    const formatTclPackagePack = (packageQty, demandQty) => {
      const pkg = parseFloat(packageQty) || 0;
      const demand = parseFloat(demandQty) || 0;
      if (pkg <= 0 || demand <= 0) return "";
      const scale = 10000;
      const totalScaled = Math.round(demand * scale);
      const pkgScaled = Math.round(pkg * scale);
      const boxes = Math.floor(totalScaled / pkgScaled);
      const remainderScaled = totalScaled - boxes * pkgScaled;
      if (remainderScaled === 0) {
        return `${pkg} x ${boxes} 箱`;
      }
      const remainder = parseFloat((remainderScaled / scale).toFixed(2));
      return `${pkg} x ${boxes} 箱 + ${remainder} 尾数`;
    };

    // 备料标签二维码：仅仓储物料编码、供应商id、数量
    const buildLabelQrContent = (item) => {
      const materialNo = item.materialNo || "";
      const supplierId = toIdStr(item.supplierId);
      const num = item.demandNum ?? item.totalDemandNum ?? "";
      return `materialNo:${materialNo};supplierId:${supplierId};num:${num}`;
    };

    // 在主页面用本地 qrcode 包生成，再写入打印窗口（不依赖外网 CDN）
    const renderLabelQrCodes = async (printWindow) => {
      const nodes = printWindow.document.querySelectorAll(".qr-canvas");
      for (const canvas of nodes) {
        let text = canvas.getAttribute("data-qr") || "";
        try {
          text = decodeURIComponent(text);
        } catch (e) {
          /* keep raw */
        }
        if (!text) continue;
        // 生成更高像素，打印时由 CSS 控制显示尺寸，避免发糊/偏小
        const pixelSize =
          text.length > 120 ? 280 : text.length > 80 ? 240 : 220;
        try {
          const dataUrl = await QRCode.toDataURL(text, {
            width: pixelSize,
            margin: 1,
            errorCorrectionLevel: "M",
            color: {
              dark: "#000000",
              light: "#FFFFFF",
            },
          });
          const img = printWindow.document.createElement("img");
          img.src = dataUrl;
          img.alt = "qr";
          img.style.width = "100%";
          img.style.height = "100%";
          img.style.objectFit = "fill";
          img.style.display = "block";
          canvas.parentNode.replaceChild(img, canvas);
        } catch (err) {
          console.error("二维码生成失败:", err, text);
        }
      }
    };

    const now = new Date();
    const pad2 = (n) => String(n).padStart(2, "0");
    const todayStr = `${now.getFullYear()}-${pad2(now.getMonth() + 1)}-${pad2(
      now.getDate(),
    )}`;
    const startTime = ref(`${todayStr}T00:00:00`);
    const endTime = ref(`${todayStr}T23:59:59`);

    const timeRange = ref([startTime.value, endTime.value]);

    const handleTimeChange = (val) => {
      if (val && val.length === 2) {
        startTime.value = val[0];
        endTime.value = val[1];
      } else {
        startTime.value = "";
        endTime.value = "";
      }
    };

    const isPrint = ref(0);
    const wareOutStatus = ref("3");
    const wareOutType = ref("0");

    const selectedData = ref([]);
    const tableRef = ref(null);

    const detailVisible = ref(false);
    const detailLoading = ref(false);
    const detailData = ref(null);

    const pages = computed(() => {
      if (total.value === 0) return 0;
      return Math.ceil(total.value / size.value);
    });

    const isAllSelected = computed(() => {
      return (
        list.value.length > 0 && selectedData.value.length === list.value.length
      );
    });

    const selectedTotalQty = computed(() => {
      return selectedData.value.reduce((sum, item) => {
        const demandNum =
          parseFloat(item.demandNum) ||
          parseFloat(item.wareOutNum) ||
          parseFloat(item.prepareNum) ||
          0;
        return sum + demandNum;
      }, 0);
    });

    // 计算表格合并单元格
    const calculateSpanMethod = ({ row, rowIndex, columnIndex }) => {
      // 只合并出库单号列（columnIndex === 2：selection、序号之后）
      if (columnIndex === 2) {
        const rows = list.value || [];
        const currentWareOutNo = row?.wareOutNo;
        // 如果前一行有相同的出库单号，说明当前行是合并组的一部分，需要隐藏
        if (
          rowIndex > 0 &&
          rows[rowIndex - 1]?.wareOutNo === currentWareOutNo
        ) {
          return {
            rowspan: 0,
            colspan: 0,
          };
        }
        // 向后查找相同的出库单号，计算合并行数
        let span = 1;
        for (let i = rowIndex + 1; i < rows.length; i++) {
          if (rows[i]?.wareOutNo === currentWareOutNo) {
            span++;
          } else {
            break;
          }
        }
        return {
          rowspan: span,
          colspan: 1,
        };
      }
      return {
        rowspan: 1,
        colspan: 1,
      };
    };

    // 生成唯一行标识（优先用加载时写入的 _rowKey，避免同单同物料不同数量行 key 冲突导致错位）
    const getRowKey = (row) =>
      row._rowKey ||
      `${row.id}_${row.wareOutNo}_${row.composeMaterialNo || ""}_${row.materialNo}_${row.materialId || ""}_${row.invCode || ""}_${row.demandNum || ""}_${row.prepareNum || ""}`;

    const isSelected = (item) => {
      return selectedData.value.some((d) => getRowKey(d) === getRowKey(item));
    };

    const truncateText = (text, maxLength) => {
      if (!text) return "";
      if (text.length <= maxLength) return text;
      return text.substring(0, maxLength) + "...";
    };

    const formatNumber = (num) => {
      const parsed = parseFloat(num);
      if (isNaN(parsed)) return "0.00";
      return parsed.toFixed(2);
    };

    const handleCopy = async (text, label) => {
      if (!text) return;
      try {
        await navigator.clipboard.writeText(text);
        toast.success(`${label}复制成功`);
      } catch (e) {
        toast.error("复制失败");
      }
    };

    const handleBack = () => router.back();

    const DELIVERY_PLAN_IMPORT_URL = "http://124.71.39.101:8765/";
    const openDeliveryPlanImport = () => {
      window.open(DELIVERY_PLAN_IMPORT_URL, "_blank", "noopener,noreferrer");
    };

    const handleSupplierSearch = async () => {
      supplierDropdown.value = true;
      await fetchSuppliers();
    };

    const fetchSuppliers = async () => {
      supplierLoading.value = true;
      try {
        const res = await getSupplierPageGtZero({
          supplierName: supplierKeyword.value.trim(),
          current: 1,
          size: 20,
        });
        supplierList.value = res?.data?.records || [];
        supplierDropdown.value = true;
      } catch (e) {
        supplierList.value = [];
      } finally {
        supplierLoading.value = false;
      }
    };

    const fetchSupplierSuggestions = async (queryString, callback) => {
      try {
        const res = await getSupplierPageGtZero({
          supplierName: queryString.trim(),
          current: 1,
          size: 20,
        });
        const data = (res?.data?.records || []).map((item) => ({
          value: item.supplierName,
          ...item,
        }));
        callback(data);
      } catch (e) {
        callback([]);
      }
    };

    const selectSupplier = (s) => {
      selectedSupplier.value = s;
      supplierDropdown.value = false;
      supplierKeyword.value = s?.supplierName || "";
    };

    const clearSelectedSupplier = () => {
      selectedSupplier.value = null;
      supplierKeyword.value = "";
      supplierList.value = [];
      supplierDropdown.value = false;
    };

    const fetchPage = async () => {
      loading.value = true;
      try {
        const params = {
          wareOutStatus: wareOutStatus.value,
          wareOutType: wareOutType.value,
          current: current.value,
          size: size.value,
        };
        if (selectedSupplier.value?.id) {
          params.supplierId = selectedSupplier.value.id;
        }
        // el-input-tag 点清除时会把 v-model 设为 undefined，必须兜底成数组
        const resourceNos = resourceNoTags.value || [];
        const invCodes = invCodeTags.value || [];
        const materialNos = materialNoTags.value || [];
        if (resourceNos.length > 0) {
          params.resourceNos = resourceNos.join(",");
        }
        if (invCodes.length > 0) {
          params.invCodes = invCodes.join(",");
        }
        if (materialNos.length > 0) {
          params.materialNos = materialNos.join(",");
        }
        if (startTime.value) {
          params.startTime = startTime.value;
        }
        if (endTime.value) {
          params.endTime = endTime.value;
        }
        params.isPrint = isPrint.value;

        const res = await getWareOutPrintPage(params);
        // 按出库单号排序，确保相同出库单号连续，便于合并显示
        const records = res?.data?.records || [];
        records.sort((a, b) => {
          const noA = a.wareOutNo || "";
          const noB = b.wareOutNo || "";
          return noA.localeCompare(noB, "en");
        });
        // 同一出库单可能有多条「同物料、不同数量」明细，id/物料都相同，必须补唯一行键，否则 el-table 会错位
        list.value = records.map((item, index) => ({
          ...item,
          _rowKey: [
            item.id,
            item.wareOutNo,
            item.composeMaterialNo || "",
            item.materialNo,
            item.materialId || "",
            item.invCode || "",
            item.demandNum || "",
            item.prepareNum || "",
            index,
          ].join("_"),
        }));
        total.value = res?.data?.total || 0;
      } catch (e) {
        toast.error(e?.message || "加载失败");
      } finally {
        loading.value = false;
      }
    };

    const handleSearch = () => {
      current.value = 1;
      selectedData.value = [];
      fetchPage();
    };

    const onPrintChange = (val) => {
      isPrint.value = Number(val);
    };

    const handleReset = () => {
      current.value = 1;
      selectedSupplier.value = null;
      supplierKeyword.value = "";
      // 重置时也强制写回数组，避免残留 el-input-tag 清除后的 undefined
      resourceNoTags.value = [];
      invCodeTags.value = [];
      materialNoTags.value = [];
      const now2 = new Date();
      const todayStr2 = `${now2.getFullYear()}-${pad2(
        now2.getMonth() + 1,
      )}-${pad2(now2.getDate())}`;
      const defaultStart = `${todayStr2}T00:00:00`;
      const defaultEnd = `${todayStr2}T23:59:59`;
      startTime.value = defaultStart;
      endTime.value = defaultEnd;
      timeRange.value = [defaultStart, defaultEnd];
      isPrint.value = 0;
      wareOutStatus.value = "3";
      wareOutType.value = "0";
      selectedData.value = [];
      fetchPage();
    };

    const handlePageChange = (page) => {
      current.value = page;
      fetchPage();
    };

    const handleSizeChange = (val) => {
      size.value = val;
      current.value = 1;
      fetchPage();
    };

    const toggleSelectItem = (item) => {
      const index = selectedData.value.findIndex(
        (d) => getRowKey(d) === getRowKey(item),
      );
      if (index > -1) {
        selectedData.value.splice(index, 1);
      } else {
        selectedData.value.push(item);
      }
    };

    const toggleSelectAll = () => {
      if (isAllSelected.value) {
        selectedData.value = [];
      } else {
        selectedData.value = [...list.value];
      }
    };

    const getRowClassName = ({ row }) => {
      return isSelected(row) ? "selected-row" : "";
    };

    let isBatchSelecting = false;

    const handleSelect = async (selection, row) => {
      if (isBatchSelecting) return;

      const wareOutNo = row.wareOutNo;
      const sameWareOutRows = list.value.filter(
        (r) => r.wareOutNo === wareOutNo,
      );

      // 检查当前出库单号下是否所有行都已选中
      const allSelected = sameWareOutRows.every((r) =>
        selection.some((s) => getRowKey(s) === getRowKey(r)),
      );

      isBatchSelecting = true;

      await nextTick();

      if (allSelected) {
        // 如果都已选中，取消全选
        sameWareOutRows.forEach((r) => {
          tableRef.value.toggleRowSelection(r, false);
        });
      } else {
        // 如果未全选，全选同出库单号的所有行
        sameWareOutRows.forEach((r) => {
          tableRef.value.toggleRowSelection(r, true);
        });
      }

      isBatchSelecting = false;
    };

    const handleSelectionChange = (selection) => {
      // 同步选中数据
      selectedData.value = [...selection];
    };

    const handleDetail = async (id) => {
      detailLoading.value = true;
      detailVisible.value = true;
      try {
        const res = await getWareOutOrderDetail(id);
        if (res?.success || res?.code === 200) {
          const data = res?.data || {};
          if (data.outDetailList && data.outDetailList.length > 0) {
            data.outDetailList.sort((a, b) => {
              const composeA = a.composeMaterialNo || "";
              const composeB = b.composeMaterialNo || "";
              if (composeA && composeB) {
                return composeA.localeCompare(composeB, "en");
              }
              if (composeA && !composeB) return -1;
              if (!composeA && composeB) return 1;
              return 0;
            });
          }
          detailData.value = data;
        } else {
          toast.error(res?.message || "获取详情失败");
        }
      } catch (e) {
        toast.error("获取详情失败");
      } finally {
        detailLoading.value = false;
      }
    };

    const getStatusText = (status) => {
      const map = {
        0: "待分配",
        1: "待收货",
        2: "待确认",
        3: "已完成",
      };
      return map[status] || "-";
    };

    const getStatusTagType = (status) => {
      const map = {
        0: "info",
        1: "warning",
        2: "primary",
        3: "success",
      };
      return map[status] || "info";
    };

    const processPrintData = (data) => {
      const materialMap = {};

      const formatDemandNum = (num) => {
        const n = parseFloat(num);
        if (Number.isInteger(n)) {
          return n.toString();
        }
        return parseFloat(n.toFixed(10)).toString();
      };
      const extractInvNumber = (invCode) => {
        const match = String(invCode).match(/[A-Za-z]*([0-9]+)$/);
        return match ? parseInt(match[1], 10) : 0;
      };
      data.sort((a, b) => {
        const numA = extractInvNumber(a.invCode);
        const numB = extractInvNumber(b.invCode);
        if (numA !== numB) {
          return numB - numA;
        }
        const letterA = (a.invCode || "").replace(/[0-9]+$/, "");
        const letterB = (b.invCode || "").replace(/[0-9]+$/, "");
        return letterB.localeCompare(letterA, "en");
      });
      data.forEach((wareOutItem) => {
        const invCode = wareOutItem.invCode || "";
        const resourceNo = wareOutItem.resourceNo || "";
        const supplierName = wareOutItem.supplierName || "";
        const supplierId = toIdStr(
          wareOutItem.supplierId || wareOutItem.wareOutDetailList?.[0]?.supplierId,
        );
        const factoryName = wareOutItem.factoryName || "";
        const createTime = wareOutItem.createTime || "";
        const wareOutNo = wareOutItem.wareOutNo;
        const plsNo = wareOutItem.plsNo;
        const wareOutId = toIdStr(wareOutItem.id);
        // 汇总时 type 取第一条的 source
        const source = wareOutItem.source;

        // 获取明细数据，优先从wareOutDetailList获取，否则从顶层字段获取
        const details =
          wareOutItem.wareOutDetailList &&
          wareOutItem.wareOutDetailList.length > 0
            ? wareOutItem.wareOutDetailList
            : [wareOutItem];

        details.forEach((detail) => {
          const materialId = toIdStr(detail.materialId);
          const key = `${resourceNo}-${materialId}-${invCode}`;

          if (!materialMap[key]) {
            const materialDesc =
              (detail.materialDesc || "").substring(0, 40) +
              ((detail.materialDesc || "").length > 40 ? "..." : "");
            materialMap[key] = {
              resourceNo: resourceNo,
              wareOutIds: new Set(),
              wareOutNos: new Set(),
              plsNos: new Set(),
              scannedPalletCodes: new Set(),
              materialId,
              materialNo: detail.materialNo,
              materialDesc: materialDesc,
              palletInfo: {},
              totalDemandNum: 0,
              totalNum: 0,
              stockNum: null,
              supplierName: supplierName,
              supplierId:
                toIdStr(detail.supplierId) || supplierId,
              packageNum: parseFloat(detail.packageNum) || 0,
              minPackageNum: parseFloat(detail.minPackageNumStr) || 0,
              factoryName: factoryName,
              invCode: invCode,
              createTime: createTime,
              source,
            };
          }
          if (wareOutId) materialMap[key].wareOutIds.add(wareOutId);
          if (wareOutNo) materialMap[key].wareOutNos.add(String(wareOutNo));
          if (plsNo) materialMap[key].plsNos.add(String(plsNo));

          // 出库数量优先从demandNum获取，否则从wareOutNum或prepareNum获取
          const demandNum =
            parseFloat(detail.demandNum) ||
            parseFloat(detail.wareOutNum) ||
            parseFloat(detail.prepareNum) ||
            0;
          materialMap[key].totalDemandNum += demandNum;

          // 当前库存取接口 stockNum，同物料取首次有效值
          if (
            materialMap[key].stockNum == null &&
            detail.stockNum != null &&
            detail.stockNum !== ""
          ) {
            const sn = parseFloat(detail.stockNum);
            if (!Number.isNaN(sn)) materialMap[key].stockNum = sn;
          }

          if (detail.recordList && detail.recordList.length > 0) {
            detail.recordList.forEach((record) => {
              const palletCode = record.palletCode || "未知";
              if (!materialMap[key].palletInfo[palletCode]) {
                materialMap[key].palletInfo[palletCode] = 0;
              }
              materialMap[key].palletInfo[palletCode] +=
                parseFloat(record.wareOutNum) || 0;
              materialMap[key].totalNum += parseFloat(record.wareOutNum) || 0;
              // scanStatus===1 表示该库位已扫，写入 scanWareCode
              if (
                Number(record.scanStatus) === 1 &&
                palletCode &&
                palletCode !== "未知"
              ) {
                materialMap[key].scannedPalletCodes.add(palletCode);
              }
            });
          }
        });
      });

      const result = [];
      Object.values(materialMap).forEach((item) => {
        const packageQty = item.packageNum;
        const demandQty = item.totalDemandNum;
        const cartonPack = formatPackagePack(packageQty, demandQty, "箱");
        const gluePack = formatPackagePack(
          item.minPackageNum,
          demandQty,
          "胶箱",
        );

        const palletList = [];
        Object.keys(item.palletInfo).forEach((palletCode) => {
          const quantity = item.palletInfo[palletCode];
          palletList.push(`${palletCode}：${quantity.toFixed(0)}`);
        });
        const palletInfoText = palletList.join("; ") || "";

        result.push({
          resourceNo: item.resourceNo,
          id: [...item.wareOutIds].join(","),
          wareOutNo: [...item.wareOutNos].join(","),
          plsNo: [...item.plsNos].join(","),
          materialId: item.materialId,
          materialNo: item.materialNo,
          materialDesc: item.materialDesc,
          palletCode: Object.keys(item.palletInfo)
            .filter((code) => code && code !== "未知")
            .join(","),
          scanWareCode: [...item.scannedPalletCodes].join(","),
          palletInfo: palletInfoText,
          demandNum: formatDemandNum(item.totalDemandNum),
          totalNum: formatDemandNum(item.totalNum),
          stockNum:
            item.stockNum == null ? "" : formatDemandNum(item.stockNum),
          supplierName: item.supplierName,
          supplierId: item.supplierId || "",
          cartonPack: cartonPack,
          gluePack: gluePack,
          factoryName: item.factoryName,
          invCode: item.invCode,
          createTime: item.createTime || "",
          source: item.source,
        });
      });

      result.sort((a, b) => {
        const numA = extractInvNumber(a.invCode);
        const numB = extractInvNumber(b.invCode);
        if (numA !== numB) {
          return numB - numA;
        }
        const letterA = (a.invCode || "").replace(/[0-9]+$/, "");
        const letterB = (b.invCode || "").replace(/[0-9]+$/, "");
        return letterB.localeCompare(letterA, "en");
      });

      return result;
    };

    // TCL月度标签数据：按出库单分别汇总；同单同物料但备注(订单号)不同则不合并
    const processTclMonthlyPrintData = (data) => {
      const formatDemandNum = (num) => {
        const n = parseFloat(num);
        if (Number.isInteger(n)) {
          return n.toString();
        }
        return parseFloat(n.toFixed(10)).toString();
      };
      const extractInvNumber = (invCode) => {
        const match = String(invCode).match(/[A-Za-z]*([0-9]+)$/);
        return match ? parseInt(match[1], 10) : 0;
      };

      const materialMap = {};

      (data || []).forEach((wareOutItem) => {
        const invCode = wareOutItem.invCode || "";
        const resourceNo = wareOutItem.resourceNo || "";
        const supplierName = wareOutItem.supplierName || "";
        const supplierId = toIdStr(
          wareOutItem.supplierId ||
            wareOutItem.wareOutDetailList?.[0]?.supplierId,
        );
        const factoryName = wareOutItem.factoryName || "";
        const createTime = wareOutItem.createTime || "";
        const wareOutNo = wareOutItem.wareOutNo;
        const plsNo = wareOutItem.plsNo;
        const wareOutId = toIdStr(wareOutItem.id);
        const source = wareOutItem.source;

        const details =
          wareOutItem.wareOutDetailList &&
          wareOutItem.wareOutDetailList.length > 0
            ? wareOutItem.wareOutDetailList
            : [wareOutItem];

        details.forEach((detail) => {
          const materialId = toIdStr(detail.materialId);
          // 备注作为订单号展示；备注不同即使同物料也不汇总
          const remark = String(detail.remark || "").trim();
          const key = `${wareOutNo || ""}-${resourceNo}-${materialId}-${invCode}-${remark}`;

          if (!materialMap[key]) {
            materialMap[key] = {
              resourceNo,
              wareOutIds: new Set(),
              wareOutNos: new Set(),
              plsNos: new Set(),
              scannedPalletCodes: new Set(),
              materialId,
              materialNo: detail.materialNo,
              materialDesc: detail.materialDesc || "",
              palletInfo: {},
              totalDemandNum: 0,
              totalNum: 0,
              supplierName,
              supplierId: toIdStr(detail.supplierId) || supplierId,
              packageNum: parseFloat(detail.packageNum) || 0,
              factoryName,
              invCode,
              createTime,
              source,
              orderRemark: remark,
            };
          }

          if (wareOutId) materialMap[key].wareOutIds.add(wareOutId);
          if (wareOutNo) materialMap[key].wareOutNos.add(String(wareOutNo));
          if (plsNo) materialMap[key].plsNos.add(String(plsNo));

          if (detail.materialDesc) {
            materialMap[key].materialDesc = detail.materialDesc;
          }
          if (detail.packageNum != null && detail.packageNum !== "") {
            materialMap[key].packageNum = parseFloat(detail.packageNum) || 0;
          }

          const demandNum =
            parseFloat(detail.demandNum) ||
            parseFloat(detail.wareOutNum) ||
            parseFloat(detail.prepareNum) ||
            0;
          materialMap[key].totalDemandNum += demandNum;

          if (detail.recordList && detail.recordList.length > 0) {
            detail.recordList.forEach((record) => {
              const palletCode = record.palletCode || "未知";
              if (!materialMap[key].palletInfo[palletCode]) {
                materialMap[key].palletInfo[palletCode] = 0;
              }
              materialMap[key].palletInfo[palletCode] +=
                parseFloat(record.wareOutNum) || 0;
              materialMap[key].totalNum += parseFloat(record.wareOutNum) || 0;
              if (
                Number(record.scanStatus) === 1 &&
                palletCode &&
                palletCode !== "未知"
              ) {
                materialMap[key].scannedPalletCodes.add(palletCode);
              }
            });
          }
        });
      });

      const result = Object.values(materialMap).map((item) => {
        const palletList = Object.keys(item.palletInfo).map((palletCode) => {
          const quantity = item.palletInfo[palletCode];
          return `${palletCode}：${quantity.toFixed(0)}`;
        });
        const demandNum = formatDemandNum(item.totalDemandNum);
        return {
          resourceNo: item.resourceNo,
          id: [...item.wareOutIds].join(","),
          wareOutNo: [...item.wareOutNos].join(","),
          plsNo: [...item.plsNos].join(","),
          materialId: item.materialId,
          materialNo: item.materialNo,
          materialDesc: item.materialDesc || "",
          palletCode: Object.keys(item.palletInfo)
            .filter((code) => code && code !== "未知")
            .join(","),
          scanWareCode: [...item.scannedPalletCodes].join(","),
          palletInfo: palletList.join("; ") || "",
          demandNum,
          totalNum: formatDemandNum(item.totalNum),
          supplierName: item.supplierName,
          supplierId: item.supplierId || "",
          factoryName: item.factoryName,
          invCode: item.invCode,
          createTime: item.createTime || "",
          source: item.source,
          tclCartonPack: formatTclPackagePack(item.packageNum, demandNum),
          orderRemark: item.orderRemark || "",
        };
      });

      return result.sort((a, b) => {
        const matCmp = String(a.materialNo || "").localeCompare(
          String(b.materialNo || ""),
          "en",
          { numeric: true, sensitivity: "base" },
        );
        if (matCmp !== 0) return matCmp;
        const remarkCmp = String(a.orderRemark || "").localeCompare(
          String(b.orderRemark || ""),
          "en",
        );
        if (remarkCmp !== 0) return remarkCmp;
        const numA = extractInvNumber(a.invCode);
        const numB = extractInvNumber(b.invCode);
        if (numA !== numB) return numB - numA;
        return String(b.invCode || "").localeCompare(
          String(a.invCode || ""),
          "en",
        );
      });
    };

    const processNonPlsPrintData = (data) => {
      const materialMap = {};

      const formatDemandNum = (num) => {
        const n = parseFloat(num);
        if (Number.isInteger(n)) {
          return n.toString();
        }
        return parseFloat(n.toFixed(10)).toString();
      };

      data.forEach((wareOutItem) => {
        const invCode = wareOutItem.invCode || "";
        const resourceNo = wareOutItem.resourceNo || "";
        const supplierName = wareOutItem.supplierName || "";
        const supplierId = toIdStr(
          wareOutItem.supplierId || wareOutItem.wareOutDetailList?.[0]?.supplierId,
        );
        const factoryName = wareOutItem.factoryName || "";
        const wareOutNo = wareOutItem.wareOutNo;
        const plsNo = wareOutItem.plsNo;
        const wareOutId = toIdStr(wareOutItem.id);
        // 汇总时 type 取第一条的 source
        const source = wareOutItem.source;

        // 获取明细数据，优先从wareOutDetailList获取，否则从顶层字段获取
        const details =
          wareOutItem.wareOutDetailList &&
          wareOutItem.wareOutDetailList.length > 0
            ? wareOutItem.wareOutDetailList
            : [wareOutItem];

        details.forEach((detail) => {
          const materialId = toIdStr(detail.materialId);
          const composeMaterialNo = detail.composeMaterialNo;
          const key = `${resourceNo}-${materialId}-${
            composeMaterialNo || ""
          }-${invCode}`;

          if (!materialMap[key]) {
            const materialDesc =
              (detail.materialDesc || "").substring(0, 40) +
              ((detail.materialDesc || "").length > 40 ? "..." : "");
            materialMap[key] = {
              resourceNo: resourceNo,
              wareOutIds: new Set(),
              wareOutNos: new Set(),
              plsNos: new Set(),
              materialId,
              materialNo: detail.materialNo,
              materialDesc: materialDesc,
              composeMaterialNo: composeMaterialNo,
              palletInfo: {},
              totalDemandNum: 0,
              totalNum: 0,
              stockNum: null,
              supplierName: supplierName,
              supplierId: toIdStr(detail.supplierId) || supplierId,
              packageNum: parseFloat(detail.packageNum) || 0,
              minPackageNum: parseFloat(detail.minPackageNumStr) || 0,
              factoryName: factoryName,
              invCode: invCode,
              source,
            };
          }
          if (wareOutId) materialMap[key].wareOutIds.add(wareOutId);
          if (wareOutNo) materialMap[key].wareOutNos.add(String(wareOutNo));
          if (plsNo) materialMap[key].plsNos.add(String(plsNo));

          // 出库数量优先从demandNum获取，否则从wareOutNum或prepareNum获取
          const demandNum =
            parseFloat(detail.demandNum) ||
            parseFloat(detail.wareOutNum) ||
            parseFloat(detail.prepareNum) ||
            0;
          materialMap[key].totalDemandNum += demandNum;

          // 当前库存取接口 stockNum，同物料取首次有效值
          if (
            materialMap[key].stockNum == null &&
            detail.stockNum != null &&
            detail.stockNum !== ""
          ) {
            const sn = parseFloat(detail.stockNum);
            if (!Number.isNaN(sn)) materialMap[key].stockNum = sn;
          }

          if (detail.recordList && detail.recordList.length > 0) {
            detail.recordList.forEach((record) => {
              const palletCode = record.palletCode || "未知";
              if (!materialMap[key].palletInfo[palletCode]) {
                materialMap[key].palletInfo[palletCode] = 0;
              }
              materialMap[key].palletInfo[palletCode] +=
                parseFloat(record.wareOutNum) || 0;
              materialMap[key].totalNum += parseFloat(record.wareOutNum) || 0;
            });
          }
        });
      });

      const result = [];
      Object.values(materialMap).forEach((item) => {
        const packageQty = item.packageNum;
        const demandQty = item.totalDemandNum;
        const cartonPack = formatPackagePack(packageQty, demandQty, "箱");
        const gluePack = formatPackagePack(
          item.minPackageNum,
          demandQty,
          "胶箱",
        );

        const palletList = [];
        Object.keys(item.palletInfo).forEach((palletCode) => {
          const quantity = item.palletInfo[palletCode];
          palletList.push(`${palletCode}：${quantity.toFixed(0)}`);
        });
        const palletInfoText = palletList.join("; ") || "";

        let displayMaterialNo = item.materialNo;
        let displayMaterialDesc = item.materialDesc;

        if (item.composeMaterialNo) {
          displayMaterialNo = item.composeMaterialNo;
          const materialNo = item.materialNo || "";
          const materialNoLen = materialNo.length;
          const totalMaxLen = 35;
          const minDescLen = 6;
          let maxDescLen = totalMaxLen - materialNoLen - 4;
          if (maxDescLen < minDescLen) {
            maxDescLen = minDescLen;
          }
          let desc = item.materialDesc || "";
          if (desc.length > maxDescLen) {
            desc = desc.substring(0, maxDescLen) + "...";
          }
          displayMaterialDesc = `${desc} (${materialNo})`;
        } else {
          const maxDescLen = 30;
          let desc = item.materialDesc || "";
          if (desc.length > maxDescLen) {
            desc = desc.substring(0, maxDescLen) + "...";
          }
          displayMaterialDesc = desc;
        }

        const isLongMaterialNo = (displayMaterialNo || "").length > 15;
        result.push({
          resourceNo: item.resourceNo,
          id: [...item.wareOutIds].join(","),
          wareOutNo: [...item.wareOutNos].join(","),
          plsNo: [...item.plsNos].join(","),
          materialId: item.materialId,
          materialNo: item.materialNo,
          materialDesc: item.materialDesc,
          displayMaterialNo: displayMaterialNo,
          displayMaterialDesc: displayMaterialDesc,
          composeMaterialNo: item.composeMaterialNo,
          isLongMaterialNo: isLongMaterialNo,
          palletCode: Object.keys(item.palletInfo)
            .filter((code) => code && code !== "未知")
            .join(","),
          palletInfo: palletInfoText,
          demandNum: formatDemandNum(item.totalDemandNum),
          totalNum: formatDemandNum(item.totalNum),
          stockNum:
            item.stockNum == null ? "" : formatDemandNum(item.stockNum),
          supplierName: item.supplierName,
          supplierId: item.supplierId || "",
          cartonPack: cartonPack,
          gluePack: gluePack,
          factoryName: item.factoryName,
          invCode: item.invCode,
          source: item.source,
        });
      });

      // 优化排序：先按子库排序，同一子库内组合物料相邻，最后按物料编码排序
      result.sort((a, b) => {
        // 第一步：按子库编码排序（倒序）
        if (a.invCode && b.invCode && a.invCode !== b.invCode) {
          const cmp = a.invCode.localeCompare(b.invCode, "en");
          return cmp === 0 ? 0 : -cmp;
        }

        // 第二步：组合物料相邻，按组合物料编码排序
        const composeA = a.composeMaterialNo || "";
        const composeB = b.composeMaterialNo || "";

        if (composeA && composeB) {
          const cmp = composeA.localeCompare(composeB, "en");
          if (cmp !== 0) return cmp;
        }

        // 有组合物料的排在前面
        if (composeA && !composeB) return -1;
        if (!composeA && composeB) return 1;

        // 第三步：按物料编码排序（倒序）
        const cmp = a.materialNo.localeCompare(b.materialNo, "en");
        return cmp === 0 ? 0 : -cmp;
      });

      return result;
    };

    const processSummaryData = (data) => {
      const supplierMap = {};

      const formatDemandNum = (num) => {
        const n = parseFloat(num);
        if (Number.isInteger(n)) {
          return n.toString();
        }
        return parseFloat(n.toFixed(10)).toString();
      };

      // 提取子库编码中的数字部分用于排序
      const extractInvNumber = (invCode) => {
        const match = String(invCode).match(/[A-Za-z]*([0-9]+)$/);
        return match ? parseInt(match[1], 10) : 0;
      };

      data.forEach((wareOutItem) => {
        const supplierName = wareOutItem.supplierName || "未知供应商";
        const invCode = wareOutItem.invCode || "未知子库";
        const resourceNo = wareOutItem.resourceNo || "";

        if (!supplierMap[supplierName]) {
          supplierMap[supplierName] = {
            invMap: {},
            totalQty: 0,
          };
        }

        if (!supplierMap[supplierName].invMap[invCode]) {
          supplierMap[supplierName].invMap[invCode] = {
            resourceMap: {},
            totalQty: 0,
          };
        }

        if (
          !supplierMap[supplierName].invMap[invCode].resourceMap[resourceNo]
        ) {
          supplierMap[supplierName].invMap[invCode].resourceMap[resourceNo] = {
            materialMap: {},
            totalQty: 0,
          };
        }

        // 获取明细数据，优先从wareOutDetailList获取，否则从顶层字段获取
        const details =
          wareOutItem.wareOutDetailList &&
          wareOutItem.wareOutDetailList.length > 0
            ? wareOutItem.wareOutDetailList
            : [wareOutItem];

        details.forEach((detail) => {
          const materialNo = detail.materialNo || "未知编码";
          const materialDesc =
            (detail.materialDesc || "").substring(0, 50) +
            ((detail.materialDesc || "").length > 50 ? "..." : "");
          // 出库数量优先从demandNum获取，否则从wareOutNum或prepareNum获取
          const demandNum =
            parseFloat(detail.demandNum) ||
            parseFloat(detail.wareOutNum) ||
            parseFloat(detail.prepareNum) ||
            0;

          if (
            !supplierMap[supplierName].invMap[invCode].resourceMap[resourceNo]
              .materialMap[materialNo]
          ) {
            supplierMap[supplierName].invMap[invCode].resourceMap[
              resourceNo
            ].materialMap[materialNo] = {
              materialNo: materialNo,
              materialDesc: materialDesc,
              demandNum: 0,
              packageNum: parseFloat(detail.packageNum) || 0,
              minPackageNum: parseFloat(detail.minPackageNumStr) || 0,
            };
          }

          supplierMap[supplierName].invMap[invCode].resourceMap[
            resourceNo
          ].materialMap[materialNo].demandNum += demandNum;
          supplierMap[supplierName].invMap[invCode].resourceMap[
            resourceNo
          ].totalQty += demandNum;
          supplierMap[supplierName].invMap[invCode].totalQty += demandNum;
          supplierMap[supplierName].totalQty += demandNum;
        });
      });

      const result = [];
      Object.keys(supplierMap).forEach((supplierName) => {
        const supplierData = supplierMap[supplierName];
        const invCodes = Object.keys(supplierData.invMap);

        invCodes.forEach((invCode, invIndex) => {
          const invData = supplierData.invMap[invCode];
          const resourceNos = Object.keys(invData.resourceMap);

          resourceNos.forEach((resourceNo, resIndex) => {
            const resourceData = invData.resourceMap[resourceNo];
            const materialNos = Object.keys(resourceData.materialMap);

            materialNos.forEach((materialNo, matIndex) => {
              const materialData = resourceData.materialMap[materialNo];
              const packageQty = materialData.packageNum;
              const demandQty = materialData.demandNum;
              const cartonPack = formatPackagePack(packageQty, demandQty, "箱");
              const gluePack = formatPackagePack(
                materialData.minPackageNum,
                demandQty,
                "胶箱",
              );

              result.push({
                supplierName:
                  resIndex === 0 && matIndex === 0 ? supplierName : "",
                isFirstSupplier:
                  resIndex === 0 && matIndex === 0 && invIndex === 0,
                invCode: resIndex === 0 && matIndex === 0 ? invCode : "",
                isFirstInv: resIndex === 0 && matIndex === 0,
                resourceNo: resourceNo,
                materialNo: materialNo,
                materialDesc: materialData.materialDesc,
                demandNum: formatDemandNum(demandQty),
                cartonPack: cartonPack,
                gluePack: gluePack,
                isGroupStart: matIndex === 0,
              });
            });
          });

          result.push({
            supplierName: "",
            isFirstSupplier: false,
            invCode: invCode,
            isFirstInv: false,
            materialNo: "",
            materialDesc: `子库${invCode}总计`,
            demandNum: formatDemandNum(invData.totalQty),
            cartonPack: "",
            gluePack: "",
            isGroupStart: false,
            isInvTotal: true,
          });
        });

        result.push({
          supplierName: "",
          isFirstSupplier: false,
          invCode: "",
          isFirstInv: false,
          materialNo: "",
          materialDesc: `供应商${supplierName}总计`,
          demandNum: formatDemandNum(supplierData.totalQty),
          cartonPack: "",
          gluePack: "",
          isGroupStart: false,
          isSupplierTotal: true,
        });
      });

      // 排序：先按子库数字部分从大到小，然后按物料编码从大到小
      result.sort((a, b) => {
        // 供应商总计行需要在最后
        if (a.isSupplierTotal) {
          return 1;
        }
        if (b.isSupplierTotal) {
          return -1;
        }

        // 比较子库
        const numA = extractInvNumber(a.invCode);
        const numB = extractInvNumber(b.invCode);
        if (numA !== numB) {
          return numB - numA;
        }
        // 子库数字部分相同，再按字母部分从大到小
        const letterA = a.invCode.replace(/[0-9]+$/, "");
        const letterB = b.invCode.replace(/[0-9]+$/, "");
        if (letterA !== letterB) {
          return letterB.localeCompare(letterA, "en");
        }

        // 同一子库内，子库计数行在普通数据行之后
        if (a.isInvTotal && !b.isInvTotal) {
          return 1;
        }
        if (!a.isInvTotal && b.isInvTotal) {
          return -1;
        }

        // 同一子库内的普通数据行，按物料编码从大到小
        if (!a.isInvTotal && !b.isInvTotal) {
          const cmp = a.materialNo.localeCompare(b.materialNo, "en");
          return cmp === 0 ? 0 : -cmp;
        }

        return 0;
      });
      return result;
    };

    const processNonPlsSummaryData = (data) => {
      const supplierMap = {};

      const formatDemandNum = (num) => {
        const n = parseFloat(num);
        if (Number.isInteger(n)) {
          return n.toString();
        }
        return parseFloat(n.toFixed(10)).toString();
      };

      data.forEach((wareOutItem) => {
        const supplierName = wareOutItem.supplierName || "未知供应商";
        const manufacturerName = wareOutItem.factoryName || "未知主机厂";
        const resourceNo = wareOutItem.resourceNo || "未知送货单";
        const invCode = wareOutItem.invCode || "未知子库";

        if (!supplierMap[supplierName]) {
          supplierMap[supplierName] = {
            resourceMap: {},
            totalQty: 0,
            materialCount: 0,
            manufacturerName: manufacturerName,
          };
        }
        if (!supplierMap[supplierName].resourceMap[resourceNo]) {
          supplierMap[supplierName].resourceMap[resourceNo] = {
            invCode: invCode,
            materialMap: {},
            totalQty: 0,
            materialCount: 0,
          };
        }

        // 获取明细数据，优先从wareOutDetailList获取，否则从顶层字段获取
        const details =
          wareOutItem.wareOutDetailList &&
          wareOutItem.wareOutDetailList.length > 0
            ? wareOutItem.wareOutDetailList
            : [wareOutItem];

        details.forEach((detail) => {
          const materialNo = detail.materialNo || "未知编码";
          const composeMaterialNo = detail.composeMaterialNo;
          const materialDesc =
            (detail.materialDesc || "").substring(0, 50) +
            ((detail.materialDesc || "").length > 50 ? "..." : "");
          // 出库数量优先从demandNum获取，否则从wareOutNum或prepareNum获取
          const demandNum =
            parseFloat(detail.demandNum) ||
            parseFloat(detail.wareOutNum) ||
            parseFloat(detail.prepareNum) ||
            0;

          const key = composeMaterialNo
            ? composeMaterialNo + "_" + materialNo
            : materialNo;
          if (
            !supplierMap[supplierName].resourceMap[resourceNo].materialMap[key]
          ) {
            supplierMap[supplierName].resourceMap[resourceNo].materialMap[key] =
              {
                materialNo: materialNo,
                materialDesc: materialDesc,
                demandNum: 0,
                packageNum: parseFloat(detail.packageNum) || 0,
                minPackageNum: parseFloat(detail.minPackageNumStr) || 0,
                composeMaterialNo: composeMaterialNo,
              };
            supplierMap[supplierName].materialCount++;
            supplierMap[supplierName].resourceMap[resourceNo].materialCount++;
          }
          supplierMap[supplierName].resourceMap[resourceNo].materialMap[
            key
          ].demandNum += demandNum;
          supplierMap[supplierName].resourceMap[resourceNo].totalQty +=
            demandNum;
          supplierMap[supplierName].totalQty += demandNum;
        });
      });

      const result = [];
      let serialNumber = 1;

      Object.keys(supplierMap).forEach((supplierName) => {
        const supplierData = supplierMap[supplierName];
        const resourceNos = Object.keys(supplierData.resourceMap);

        let isFirstResource = true;
        resourceNos.forEach((resourceNo) => {
          const resourceData = supplierData.resourceMap[resourceNo];
          const invCode = resourceData.invCode;
          const materialKeys = Object.keys(resourceData.materialMap);

          const materials = materialKeys.map((key) => {
            return resourceData.materialMap[key];
          });

          materials.sort((a, b) => {
            const composeA = a.composeMaterialNo || "";
            const composeB = b.composeMaterialNo || "";

            if (composeA && composeB) {
              const cmp = composeA.localeCompare(composeB, "en");
              if (cmp !== 0) return cmp;
            }

            if (composeA && !composeB) return -1;
            if (!composeA && composeB) return 1;

            const cmp = a.materialNo.localeCompare(b.materialNo, "en");
            return cmp === 0 ? 0 : -cmp;
          });

          materials.forEach((materialData, matIndex) => {
            const packageQty = materialData.packageNum;
            const demandQty = materialData.demandNum;
            const cartonPack = formatPackagePack(packageQty, demandQty, "箱");
            const gluePack = formatPackagePack(
              materialData.minPackageNum,
              demandQty,
              "胶箱",
            );

            result.push({
              serialNumber: serialNumber,
              supplierName:
                isFirstResource && matIndex === 0 ? supplierName : "",
              manufacturerName:
                isFirstResource && matIndex === 0
                  ? supplierData.manufacturerName
                  : "",
              invCode: invCode,
              resourceNo: resourceNo,
              materialNo: materialData.materialNo,
              materialDesc: materialData.materialDesc,
              demandNum: formatDemandNum(demandQty),
              cartonPack: cartonPack,
              gluePack: gluePack,
              isGroupStart: matIndex === 0,
              composeMaterialNo: materialData.composeMaterialNo,
            });
            serialNumber++;
          });

          result.push({
            serialNumber: "",
            supplierName: "",
            manufacturerName: "",
            invCode: invCode,
            resourceNo: resourceNo,
            materialNo: "",
            materialDesc: invCode + " 计数",
            demandNum: "",
            cartonPack: "",
            gluePack: "",
            isInvTotal: true,
          });
          isFirstResource = false;
        });
        result.push({
          serialNumber: "",
          supplierName: "",
          manufacturerName: "",
          invCode: "",
          resourceNo: "",
          materialNo: "",
          materialDesc: supplierName + " 总计",
          demandNum: supplierData.materialCount,
          cartonPack: "",
          gluePack: "",
          isSupplierTotal: true,
        });
      });

      return result;
    };

    const openPrintPreview = (
      printData,
      paperSize,
      originalData = [],
      splitInvCodes = [],
      ids = [],
    ) => {
      const width = paperSize?.width || 100;
      const height = paperSize?.height || 90;
      const customText = paperSize?.customText || "";
      // 仅布局：铺满纸张 + 四周小边距；字体大小保持原逻辑不变
      const cardPadMm = 1.5;
      const isH70 = height === 70;
      const isH75 = height === 75;
      const isH80 = height === 80;
      // 100×80 左侧标题略小，避免贴边被裁切；70/75/90 保持原字号
      const labelFont = isH70
        ? "18px"
        : isH75
          ? "20px"
          : isH80
            ? "18px"
            : "22px";
      const labelPadLeft = isH70 ? "3px" : isH80 ? "6px" : "5px";
      const valueFont = isH70
        ? "18px"
        : isH75
          ? "20px"
          : isH80
            ? "19px"
            : "22px";
      // 二维码按前两行区域接近铺满（约标签高度 30%），四周仅留约 0.8mm
      const qrSizeMm = Math.max(
        18,
        Number(
          (
            Math.min((width - cardPadMm * 2) * 0.27, (height - cardPadMm * 2) * 0.3) -
            0.8
          ).toFixed(1),
        ),
      );
      const printWindow = window.open("", "_blank");
      const now = new Date();
      const printTime = `${(now.getMonth() + 1)
        .toString()
        .padStart(2, "0")}/${now.getDate().toString().padStart(2, "0")} ${now
        .getHours()
        .toString()
        .padStart(2, "0")}:${now.getMinutes().toString().padStart(2, "0")}`;

      let summaryHtml = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8">
          <title>打印预览</title>
          <style>
            * {
              box-sizing: border-box;
              margin: 0;
              padding: 0;
            }
            @media print {
              @page {
                size: ${width}mm ${height}mm;
                margin: 0;
                padding: 0;
              }
              html, body {
                margin: 0 !important;
                padding: 0 !important;
                width: ${width}mm;
                font-family: "Microsoft YaHei", Arial, sans-serif;
                font-size: 14px;
              }
              .print-toolbar {
                display: none !important;
              }
              .print-content {
                margin: 0 !important;
                padding: 0 !important;
                width: ${width}mm !important;
              }
              .label-card {
                page-break-after: always !important;
                break-after: page !important;
                page-break-inside: avoid !important;
                overflow: hidden;
                margin: 0 !important;
                padding: ${cardPadMm}mm !important;
                width: ${width}mm !important;
                height: ${height}mm !important;
              }
              .label-table {
                width: 100% !important;
                height: 100% !important;
              }
            }
            html, body {
              margin: 0;
              padding: 0;
              font-family: "Microsoft YaHei", Arial, sans-serif;
              background: transparent;
            }
            body {
              width: ${width}mm;
            }
            .label-card {
              width: ${width}mm;
              height: ${height}mm;
              margin: 0;
              padding: ${cardPadMm}mm;
              background: transparent;
              box-shadow: none;
              border: none;
              box-sizing: border-box;
              page-break-after: always;
              overflow: hidden;
            }
            .label-table {
              width: 100%;
              height: 100%;
              margin: 0;
              padding: 0;
              border-collapse: collapse;
              border: 2px solid #000;
              table-layout: fixed;
            }
            .label-table td {
              border: 1px solid #000;
              padding: ${isH70 ? "1px 2px" : "2px 4px"};
              vertical-align: middle;
              text-align: center;
              font-size: ${
                isH70 ? "9px" : isH75 ? "10px" : isH80 ? "10px" : "11px"
              };
              line-height: 1.1;
            }
            .label-table tr {
              height: 11.66%;
            }

            .label-cell {
              font-size: ${labelFont}!important;
              text-align: left;
              padding-left: ${labelPadLeft} !important;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: clip;
              width: ${isH80 ? "30%" : "28%"};
            }
            .supplier-cell {
              vertical-align: top;
              padding-top: 4px;
              font-size: ${labelFont} !important;
              font-weight: bold !important;
              white-space: normal;
              word-break: break-word;
              width: 50%;
              position: relative;
            }
            .qr-cell {
              vertical-align: middle;
              text-align: center !important;
              width: ${qrSizeMm + 1.6}mm;
              padding: 0.8mm !important;
              padding-left: 0.8mm !important;
            }
            .qr-code-wrapper {
              display: block;
              width: ${qrSizeMm}mm;
              height: ${qrSizeMm}mm;
              margin: 0 auto;
              background: #fff;
              box-sizing: border-box;
              overflow: hidden;
            }
            .qr-code-wrapper img,
            .qr-code-wrapper canvas {
              display: block;
              width: ${qrSizeMm}mm !important;
              height: ${qrSizeMm}mm !important;
              object-fit: fill;
              image-rendering: pixelated;
            }
            .time-label-cell {
              font-size: ${
                isH70 ? "12px" : isH75 ? "14px" : isH80 ? "14px" : "16px"
              } !important;
              width: 22%;
            }
            .time-value-cell {
              font-size: ${
                isH70 ? "12px" : isH75 ? "14px" : isH80 ? "14px" : "16px"
              } !important;
              width: 28%;
            }
            .label-table tr:first-child,
            .label-table tr:nth-child(2) {
              height: 15% !important;
            }
            .large-font-cell {
              font-size: ${
                isH70 ? "22px" : isH75 ? "24px" : isH80 ? "24px" : "26px"
              } !important;
              font-weight: bold !important;
              padding-top: 0 !important;
              padding-bottom: 0 !important;
              line-height: 1 !important;
            }
            .pack-cell {
              font-size: ${valueFont} !important;
              padding-top: 0 !important;
              padding-bottom: 0 !important;
              font-weight: bold !important;
              line-height: 1 !important;
            }
            .value-cell {
              font-size: ${valueFont} !important;
              text-align: left;
              font-weight: bold !important;
            }
            .value-cell-width {
              width: 22%;
            }
            .value-bold {
              font-size: ${isH75 || isH80 ? "12px" : "14px"} !important;
              font-weight: bold !important;
            }
            .supplier-name-cell {
              font-size: ${isH75 || isH80 ? "22px" : "26px"} !important;
              font-weight: 800 !important;
              padding-top: 0 !important;
              padding-bottom: 0 !important;
              line-height: 1 !important;
            }
            .material-no-cell {
              font-size: ${isH75 || isH80 ? "22px" : "26px"} !important;
              font-weight: 800 !important;
              letter-spacing: ${isH80 ? "1px" : "2px"} !important;
              text-align: center;
              padding-top: 0 !important;
              padding-bottom: 0 !important;
              line-height: 1 !important;
            }
            .desc-cell {
              font-size: ${isH75 ? "20px" : isH80 ? "20px" : "24px"}!important;
              font-weight: bold !important;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
              text-align: left;
              padding-top: 0 !important;
              padding-bottom: 0 !important;
              line-height: 1 !important;
              font-weight: bold !important;
              max-width: 0;
            }
            .print-toolbar {
              position: fixed;
              top: 0;
              left: 0;
              right: 0;
              background: #333;
              padding: 10px 20px;
              display: flex;
              justify-content: flex-end;
              gap: 12px;
              z-index: 1000;
            }
            .toolbar-btn {
              padding: 8px 20px;
              border: none;
              border-radius: 4px;
              font-size: 14px;
              cursor: pointer;
              transition: all 0.2s;
            }
            .toolbar-btn.primary {
              background: #1890ff;
              color: white;
            }
            .toolbar-btn.primary:hover {
              background: #096dd9;
            }
            .toolbar-btn.secondary {
              background: #fff;
              color: #333;
            }
            .toolbar-btn.secondary:hover {
              background: #e6e6e6;
            }
            .toolbar-btn.tertiary {
              background: #fa8c16;
              color: white;
            }
            .toolbar-btn.tertiary:hover {
              background: #d46b08;
            }
          </style>
        </head>
        <body style="margin: 0; padding: 0;">
          <div class="print-toolbar">
            <button class="toolbar-btn secondary" onclick="handlePrintClick()">打印标签</button>
            <button class="toolbar-btn primary" onclick="openSummaryPrint()">表格汇总</button>

          </div>
          <div class="print-content" style="margin-top: 50px;">
      `;

      printData.forEach((item, index) => {
        const labelNo = index + 1;
        const qrContent = encodeURIComponent(buildLabelQrContent(item));
        summaryHtml += `
          <div class="label-card">
            <table class="label-table">
              <tr>
                <td class="label-cell qr-cell" rowspan="2">
                  <div class="qr-code-wrapper">
                    <canvas class="qr-canvas" data-qr="${qrContent}"></canvas>
                  </div>
                </td>
                <td class="value-cell value-bold">NO${labelNo}</td>
                <td class="value-cell supplier-name-cell" colspan="2">${item.supplierName || ""}${customText ? customText : ""}</td>
              </tr>
              <tr>
                <td class="value-cell time-value-cell">${printTime}</td>
                <td class="label-cell">仓库</td>
                <td class="value-cell value-cell-width"><strong>${
                  item.invCode || ""
                }</strong></td>
              </tr>
              <tr>
                <td class="label-cell">物料编码</td>
                <td class="material-no-cell" colspan="3">${
                  "\u200B" + (item.materialNo || "")
                }</td>
              </tr>
              <tr>
                <td class="label-cell">物料描述</td>
                <td class="desc-cell" colspan="3">${
                  item.materialDesc || ""
                }</td>
              </tr>
              <tr>
                <td class="label-cell">需求数量</td>
                <td class="value-cell large-font-cell"><strong>${
                  item.demandNum
                }</strong></td>
                <td class="label-cell">当前库存</td>
                <td class="value-cell value-cell-width"><strong>${
                  item.stockNum ?? ""
                }</strong></td>
              </tr>
              <tr>
                <td class="label-cell">纸箱包装</td>
                <td class="value-cell pack-cell" colspan="3"><strong>${
                  item.cartonPack || ""
                }</strong></td>
              </tr>
              <tr>
                <td class="label-cell">胶框包装</td>
                <td class="value-cell pack-cell" colspan="3"><strong>${
                  item.gluePack || ""
                }</strong></td>
              </tr>
              <tr>
                <td class="label-cell">库存信息</td>
                <td class="value-cell large-font-cell" colspan="3"><strong>${
                  item.palletInfo || ""
                }</strong></td>
              </tr>
            </table>
          </div>
        `;
      });

      summaryHtml +=
        `
          </div>
          <script>
            const originalData = ` +
        JSON.stringify(originalData) +
        `;
            
            var selectedSplitInvCodes = ` +
        JSON.stringify(splitInvCodes) +
        `;
            
            var printIds = ` +
        JSON.stringify(ids) +
        `;
            
            function processSummaryData(data, materialAsc) {
              const supplierMap = {};
              var totalMaterialCount = 0;
              
              function formatDemandNum(num) {
                var n = parseFloat(num);
                if (Number.isInteger(n)) {
                  return n.toString();
                }
                return parseFloat(n.toFixed(10)).toString();
              }

              function formatPackagePack(packageQty, demandQty, boxSuffix) {
                boxSuffix = boxSuffix || "箱";
                var pkg = parseFloat(packageQty) || 0;
                var demand = parseFloat(demandQty) || 0;
                if (pkg <= 0 || demand <= 0) return "";
                var scale = 10000;
                var totalScaled = Math.round(demand * scale);
                var pkgScaled = Math.round(pkg * scale);
                var boxes = Math.floor(totalScaled / pkgScaled);
                var remainderScaled = totalScaled - boxes * pkgScaled;
                if (remainderScaled === 0) {
                  return pkg + " * " + boxes + boxSuffix;
                }
                var remainder = parseFloat((remainderScaled / scale).toFixed(2));
                return pkg + " * " + boxes + boxSuffix + " + " + remainder + "只";
              }
              
              // 提取子库编码中的数字部分用于排序
              function extractInvNumber(invCode) {
                var match = String(invCode).match(/[A-Za-z]*([0-9]+)$/);
                return match ? parseInt(match[1], 10) : 0;
              }
              
              data.forEach(function(wareOutItem) {
                const supplierName = wareOutItem.supplierName || "未知供应商";
                const manufacturerName = wareOutItem.factoryName || "未知主机厂";
                const invCode = wareOutItem.invCode || "未知子库";
                if (!supplierMap[supplierName]) {
                  supplierMap[supplierName] = { invMap: {}, totalQty: 0, materialCount: 0, manufacturerName: manufacturerName };
                }
                if (!supplierMap[supplierName].invMap[invCode]) {
                  supplierMap[supplierName].invMap[invCode] = { materialMap: {}, totalQty: 0, materialCount: 0 };
                }
                
                // 获取明细数据，优先从wareOutDetailList获取，否则从顶层字段获取
                var details = wareOutItem.wareOutDetailList && wareOutItem.wareOutDetailList.length > 0 ? wareOutItem.wareOutDetailList : [wareOutItem];
                
                details.forEach(function(detail) {
                  const materialNo = detail.materialNo || "未知编码";
                  const materialDesc = (detail.materialDesc || "").substring(0, 50) + ((detail.materialDesc || "").length > 50 ? "..." : "");
                  // 出库数量优先从demandNum获取，否则从wareOutNum或prepareNum获取
                  const demandNum = parseFloat(detail.demandNum) || parseFloat(detail.wareOutNum) || parseFloat(detail.prepareNum) || 0;
                  if (!supplierMap[supplierName].invMap[invCode].materialMap[materialNo]) {
                    supplierMap[supplierName].invMap[invCode].materialMap[materialNo] = {
                      materialNo: materialNo,
                      materialDesc: materialDesc,
                      demandNum: 0,
                      packageNum: parseFloat(detail.packageNum) || 0,
                      minPackageNum: parseFloat(detail.minPackageNumStr) || 0,
                      palletInfo: {},
                    };
                    supplierMap[supplierName].materialCount++;
                    supplierMap[supplierName].invMap[invCode].materialCount++;
                    totalMaterialCount++;
                  }
                  supplierMap[supplierName].invMap[invCode].materialMap[materialNo].demandNum += demandNum;
                  supplierMap[supplierName].invMap[invCode].totalQty += demandNum;
                  supplierMap[supplierName].totalQty += demandNum;
                  // 汇总库位信息（与备料标签库存信息一致）
                  if (detail.recordList && detail.recordList.length > 0) {
                    detail.recordList.forEach(function(record) {
                      var palletCode = record.palletCode || "未知";
                      var qty = parseFloat(record.wareOutNum) || 0;
                      if (!supplierMap[supplierName].invMap[invCode].materialMap[materialNo].palletInfo[palletCode]) {
                        supplierMap[supplierName].invMap[invCode].materialMap[materialNo].palletInfo[palletCode] = 0;
                      }
                      supplierMap[supplierName].invMap[invCode].materialMap[materialNo].palletInfo[palletCode] += qty;
                    });
                  }
                });
              });
              var result = [];
              var serialNumber = 1;
              Object.keys(supplierMap).forEach(function(supplierName) {
                var supplierData = supplierMap[supplierName];
                var invCodes = Object.keys(supplierData.invMap);
                var isFirstInv = true;
                invCodes.forEach(function(invCode, invIndex) {
                  var invData = supplierData.invMap[invCode];
                  var materialNos = Object.keys(invData.materialMap);
                  var isSplit = selectedSplitInvCodes.indexOf(invCode) > -1;
                  materialNos.forEach(function(materialNo, matIndex) {
                    var materialData = invData.materialMap[materialNo];
                    var packageQty = materialData.packageNum;
                    var demandQty = materialData.demandNum;
                    var cartonPack = formatPackagePack(packageQty, demandQty, "箱");
                    var gluePack = formatPackagePack(materialData.minPackageNum, demandQty, "胶箱");
                    var palletList = [];
                    Object.keys(materialData.palletInfo || {}).forEach(function(palletCode) {
                      palletList.push(palletCode + "：" + materialData.palletInfo[palletCode].toFixed(0));
                    });
                    var palletInfo = palletList.join("; ") || "";
                    result.push({
                      serialNumber: serialNumber,
                      supplierName: isFirstInv && matIndex === 0 ? supplierName : "",
                      manufacturerName: isFirstInv && matIndex === 0 ? supplierData.manufacturerName : "",
                      invCode: invCode,
                      isSplit: isSplit,
                      materialNo: materialNo,
                      materialDesc: materialData.materialDesc,
                      demandNum: formatDemandNum(demandQty),
                      cartonPack: cartonPack,
                      gluePack: gluePack,
                      palletInfo: palletInfo,
                      isGroupStart: matIndex === 0,
                    });
                    serialNumber++;
                  });
                  result.push({
                    serialNumber: "", supplierName: "", manufacturerName: "", invCode: invCode, isSplit: false, materialNo: "", materialDesc: invCode + " 计数",
                    demandNum: "", cartonPack: "", gluePack: "", palletInfo: "",
                    isInvTotal: true,
                  });
                  isFirstInv = false;
                });
                result.push({
                  serialNumber: "", supplierName: "", manufacturerName: "", invCode: "", isSplit: false, materialNo: "", materialDesc: supplierName + " 总计",
                  demandNum: supplierData.materialCount, cartonPack: "", gluePack: "", palletInfo: "",
                  isSupplierTotal: true,
                });
              });
              result.push({
                serialNumber: "", supplierName: "", manufacturerName: "", invCode: totalMaterialCount, isSplit: false, materialNo: "", materialDesc: "总计数",
                demandNum: "", cartonPack: "", gluePack: "", palletInfo: "",
                isGrandTotal: true,
              });
              
              // 排序：先按子库数字部分从大到小，然后按物料编码（默认从大到小，materialAsc 为真则从小到大）
              result.sort(function(a, b) {
                // 供应商总计行需要在最后
                if (a.isSupplierTotal) {
                  return 1;
                }
                if (b.isSupplierTotal) {
                  return -1;
                }
                
                // 比较子库
                var numA = extractInvNumber(a.invCode);
                var numB = extractInvNumber(b.invCode);
                if (numA !== numB) {
                  return numB - numA;
                }
                // 子库数字部分相同，再按字母部分从大到小
                var letterA = a.invCode.replace(/[0-9]+$/, "");
                var letterB = b.invCode.replace(/[0-9]+$/, "");
                if (letterA !== letterB) {
                  return letterB.localeCompare(letterA, "en");
                }
                
                // 同一子库内，子库计数行在普通数据行之后
                if (a.isInvTotal && !b.isInvTotal) {
                  return 1;
                }
                if (!a.isInvTotal && b.isInvTotal) {
                  return -1;
                }
                
                // 同一子库内的普通数据行，按物料编码排序
                if (!a.isInvTotal && !b.isInvTotal) {
                  var cmp = a.materialNo.localeCompare(b.materialNo, "en");
                  if (cmp === 0) return 0;
                  return materialAsc ? cmp : -cmp;
                }
                
                return 0;
              });

              // 排序后再按当前展示顺序重排序号，并修正子库分组起始标记
              var sn = 1;
              var lastInvCode = null;
              result.forEach(function(item) {
                if (item.isInvTotal || item.isSupplierTotal || item.isGrandTotal) {
                  item.serialNumber = "";
                  return;
                }
                item.serialNumber = sn++;
                item.isGroupStart = item.invCode !== lastInvCode;
                lastInvCode = item.invCode;
              });
              return result;
            }

            window.__plsOriginalData = originalData;
            window.__plsBuildSummaryRows = function(summaryData) {
              var html = "";
              summaryData.forEach(function(item) {
                if (item.isGrandTotal) {
                  html += "<tr class='total-row'><td></td><td></td><td></td><td style='font-size:14px;'>" + item.materialDesc + "</td><td style='font-size:14px;'>" + item.invCode + "</td><td></td><td></td><td></td><td></td></tr>";
                } else if (item.isSupplierTotal) {
                  html += "<tr class='total-row'><td colspan='3' style='text-align:left;padding-left:40px;'>" + item.materialDesc + "</td><td>" + item.demandNum + "</td><td></td><td></td><td></td><td></td><td></td></tr>";
                } else if (item.isInvTotal) {
                  html += "<tr class='total-row' style='background:#e6f7ff;'><td></td><td></td><td></td><td>" + item.materialDesc + "</td><td>" + item.invCode + "</td><td></td><td></td><td></td><td></td></tr>";
                } else {
                  var rowClass = item.isGroupStart ? "inv-group" : "material-row";
                  var splitText = item.isSplit ? "<span class='split-yes'>拆包</span>" : "";
                  html += "<tr class='" + rowClass + "'><td class='col-serial'>" + (item.serialNumber || "") + "</td><td class='col-material-no'>" + (item.materialNo || "") + "</td><td class='col-qty'>" + (item.demandNum || "") + "</td><td class='col-desc' style='text-align:left;'>" + (item.materialDesc || "") + "</td><td class='col-inv'>" + (item.invCode || "") + "</td><td class='col-split'>" + splitText + "</td><td class='col-carton'>" + (item.cartonPack || "") + "</td><td class='col-glue'>" + (item.gluePack || "") + "</td><td class='col-pallet'>" + (item.palletInfo || "") + "</td></tr>";
                }
              });
              return html;
            };
            
            function setSplitInvCodes(codes) {
              selectedSplitInvCodes = codes || [];
            }
            
            async function handlePrintClick() {
              if (confirm("确定要打印标签吗？")) {
                if (window.opener && window.opener.__batchUpdateIsPrint) {
                  try {
                    await window.opener.__batchUpdateIsPrint(printIds);
                  } catch (err) {
                    console.error("批量修改打印状态失败:", err);
                  }
                }
                window.print();
              }
            }
            
            function openSummaryPrint() {
              var summaryData = processSummaryData(originalData, false);
              var printWindow = window.open("", "_blank");
              var now = new Date();
              var printTime = now.getFullYear() + "/" + String(now.getMonth() + 1).padStart(2, "0") + "/" + String(now.getDate()).padStart(2, "0") + " " + String(now.getHours()).padStart(2, "0") + ":" + String(now.getMinutes()).padStart(2, "0");
              
              var firstItem = summaryData.find(function(item) { return item.supplierName && item.supplierName !== ''; });
              var supplierName = firstItem ? firstItem.supplierName : '';
              var manufacturerName = firstItem ? firstItem.manufacturerName : '';
              
              var html = "<!DOCTYPE html><html><head><meta charset='UTF-8'><title>出库汇总打印预览</title><style>*{box-sizing:border-box;margin:0;padding:0;}@media print{@page{size:A4 landscape;margin:15mm;}body{font-family:'Microsoft YaHei',Arial,sans-serif;font-size:10px;}.copy-toolbar{display:none!important;}}body{font-family:'Microsoft YaHei',Arial,sans-serif;font-size:12px;padding:20px;}.summary-container{max-width:100%;margin:0 auto;}.print-header{margin-bottom:15px;padding-bottom:10px;border-bottom:2px solid #333;}.header-title{font-size:20px;font-weight:bold;color:#333;margin-bottom:10px;text-align:center;}.header-row{display:flex;justify-content:space-between;margin-bottom:8px;font-size:14px;}.header-left{flex:1;text-align:left;font-weight:bold;}.header-right{flex:1;text-align:right;color:#666;}.summary-table{width:100%;border-collapse:collapse;margin-top:10px;}.summary-table th,.summary-table td{border:1px solid #333;padding:6px 4px;text-align:center;vertical-align:middle;font-size:11px;word-break:break-all;}.summary-table th{background:#f5f5f5;font-weight:bold;font-size:12px;}.inv-group{background:#f0f8ff;}.total-row{background:#fff3cd;font-weight:bold;}.col-serial{width:5%;}.col-material-no{width:14%;}.col-qty{width:8%;}.col-desc{width:16%;}.col-inv{width:8%;}.col-split{width:6%;}.col-carton{width:12%;font-size:13px;font-weight:bold;padding:8px 4px;}.col-glue{width:12%;font-size:13px;font-weight:bold;padding:8px 4px;}.col-pallet{width:19%;font-size:12px;font-weight:bold;padding:8px 4px;text-align:left;}.split-yes{color:#dc2626;font-weight:bold;}.print-footer{text-align:center;margin-top:20px;padding-top:10px;border-top:1px solid #ccc;font-size:10px;color:#999;}.copy-toolbar{text-align:right;margin-bottom:10px;}.copy-btn{padding:6px 16px;border:1px solid #1890ff;border-radius:4px;background:white;color:#1890ff;font-size:12px;cursor:pointer;margin-left:8px;}.copy-btn:hover{background:#1890ff;color:white;}.copy-btn:first-child{margin-left:0;}</style></head><body><div class='summary-container'><div class='print-header'><div class='header-title'>出库单汇总表</div><div class='header-row'><div class='header-left'>供应商：" + supplierName + " | 主机厂：" + manufacturerName + "</div><div class='header-right'>打印时间：" + printTime + "</div></div></div><div class='copy-toolbar'><button class='copy-btn' onclick='copySummaryToExcel()'>复制到Excel</button><button id='sortBtn' class='copy-btn' onclick='toggleMaterialSort()'>编码从小到大</button></div><table class='summary-table'><thead><tr><th class='col-serial'>序号</th><th class='col-material-no'>物料编码</th><th class='col-qty'>汇总数量</th><th class='col-desc'>物料描述</th><th class='col-inv'>子库</th><th class='col-split'>是否拆包</th><th class='col-carton'>纸箱包装</th><th class='col-glue'>胶框包装</th><th class='col-pallet'>库位信息</th></tr></thead><tbody>" + window.__plsBuildSummaryRows(summaryData) + "</tbody></table><div class='print-footer'>打印时间：" + printTime + " | 出库单汇总表</div></div></body></html>";
              
              printWindow.document.write(html);
              printWindow.document.close();
              printWindow.focus();
              
              // Inject copy / sort functions into the new window
              var copyFn = "var __materialAsc=false;function toggleMaterialSort(){if(!window.opener||!window.opener.processSummaryData||!window.opener.__plsOriginalData||!window.opener.__plsBuildSummaryRows)return;__materialAsc=!__materialAsc;var data=window.opener.processSummaryData(window.opener.__plsOriginalData,__materialAsc);var tbody=document.querySelector('.summary-table tbody');if(tbody)tbody.innerHTML=window.opener.__plsBuildSummaryRows(data);var btn=document.getElementById('sortBtn');if(btn)btn.textContent=__materialAsc?'\u7f16\u7801\u4ece\u5927\u5230\u5c0f':'\u7f16\u7801\u4ece\u5c0f\u5230\u5927'}function copySummaryToExcel(){var tb=document.querySelector('.summary-table tbody');if(!tb)return;var th=document.querySelector('.summary-table thead');var L=[],T=String.fromCharCode(9),N=String.fromCharCode(10);var hl=document.querySelector('.header-row .header-left');var hr=document.querySelector('.header-row .header-right');var hVals=['','','','','','','','',''];if(hl){var hparts=hl.textContent.trim().split('|');var supplier='',manufacturer='';for(var hpi=0;hpi<hparts.length;hpi++){var kv=hparts[hpi].trim().split('：');if(hparts[hpi].trim().indexOf('供应商')!==-1){supplier=kv[kv.length-1].trim()}else if(hparts[hpi].trim().indexOf('主机厂')!==-1){manufacturer=kv[kv.length-1].trim()}}hVals[0]=manufacturer;hVals[1]=supplier}if(hr){var kv=hr.textContent.trim().split('：');hVals[6]=kv[kv.length-1].trim()}L.push(hVals.join(T));if(th){var hc=th.querySelectorAll('tr th'),h=[];hc.forEach(function(c){h.push(c.textContent.trim())});L.push(h.join(T))}var rw=tb.querySelectorAll('tr');rw.forEach(function(r){var cl=r.querySelectorAll('td'),rd=[];cl.forEach(function(c){rd.push(c.textContent.trim())});L.push(rd.join(T))});var tx=L.join(N);var btn=document.querySelector('.copy-btn');if(btn)btn.textContent='\u2713';if(!navigator.clipboard){var ta=document.createElement('textarea');ta.value=tx;document.body.appendChild(ta);ta.select();document.execCommand('copy');document.body.removeChild(ta);if(btn)setTimeout(function(){btn.textContent='\u590D\u5236\u5230Excel'},1500);return}navigator.clipboard.writeText(tx).then(function(){if(btn)setTimeout(function(){btn.textContent='\u590D\u5236\u5230Excel'},1500)})['catch'](function(){var ta=document.createElement('textarea');ta.value=tx;document.body.appendChild(ta);ta.select();document.execCommand('copy');document.body.removeChild(ta);if(btn)setTimeout(function(){btn.textContent='\u590D\u5236\u5230Excel'},1500)})}";
              var cs = printWindow.document.createElement("script");
              cs.textContent = copyFn;
              printWindow.document.body.appendChild(cs);
              
              setTimeout(function() { printWindow.print(); }, 250);
            }
            
            function processNonPlsSummaryData(data) {
              var supplierMap = {};
              
              function formatDemandNum(num) {
                var n = parseFloat(num);
                if (Number.isInteger(n)) {
                  return n.toString();
                }
                return parseFloat(n.toFixed(10)).toString();
              }

              function formatPackagePack(packageQty, demandQty, boxSuffix) {
                boxSuffix = boxSuffix || "箱";
                var pkg = parseFloat(packageQty) || 0;
                var demand = parseFloat(demandQty) || 0;
                if (pkg <= 0 || demand <= 0) return "";
                var scale = 10000;
                var totalScaled = Math.round(demand * scale);
                var pkgScaled = Math.round(pkg * scale);
                var boxes = Math.floor(totalScaled / pkgScaled);
                var remainderScaled = totalScaled - boxes * pkgScaled;
                if (remainderScaled === 0) {
                  return pkg + " * " + boxes + boxSuffix;
                }
                var remainder = parseFloat((remainderScaled / scale).toFixed(2));
                return pkg + " * " + boxes + boxSuffix + " + " + remainder + "只";
              }
              
              data.forEach(function(wareOutItem) {
                var supplierName = wareOutItem.supplierName || "未知供应商";
                var manufacturerName = wareOutItem.factoryName || "未知主机厂";
                var resourceNo = wareOutItem.resourceNo || "未知送货单";
                var invCode = wareOutItem.invCode || "未知子库";
                
                if (!supplierMap[supplierName]) {
                  supplierMap[supplierName] = { resourceMap: {}, totalQty: 0, materialCount: 0, manufacturerName: manufacturerName };
                }
                if (!supplierMap[supplierName].resourceMap[resourceNo]) {
                  supplierMap[supplierName].resourceMap[resourceNo] = { invCode: invCode, materialMap: {}, totalQty: 0, materialCount: 0 };
                }
                
                // 获取明细数据，优先从wareOutDetailList获取，否则从顶层字段获取
                var details = wareOutItem.wareOutDetailList && wareOutItem.wareOutDetailList.length > 0 ? wareOutItem.wareOutDetailList : [wareOutItem];
                
                details.forEach(function(detail) {
                  var materialNo = detail.materialNo || "未知编码";
                  var composeMaterialNo = detail.composeMaterialNo;
                  var materialDesc = (detail.materialDesc || "").substring(0, 50) + ((detail.materialDesc || "").length > 50 ? "..." : "");
                  // 出库数量优先从demandNum获取，否则从wareOutNum或prepareNum获取
                  var demandNum = parseFloat(detail.demandNum) || parseFloat(detail.wareOutNum) || parseFloat(detail.prepareNum) || 0;
                  
                  var key = composeMaterialNo ? composeMaterialNo + "_" + materialNo : materialNo;
                  if (!supplierMap[supplierName].resourceMap[resourceNo].materialMap[key]) {
                    supplierMap[supplierName].resourceMap[resourceNo].materialMap[key] = {
                      materialNo: materialNo,
                      materialDesc: materialDesc,
                      demandNum: 0,
                      packageNum: parseFloat(detail.packageNum) || 0,
                      minPackageNum: parseFloat(detail.minPackageNumStr) || 0,
                      composeMaterialNo: composeMaterialNo,
                    };
                    supplierMap[supplierName].materialCount++;
                    supplierMap[supplierName].resourceMap[resourceNo].materialCount++;
                  }
                  supplierMap[supplierName].resourceMap[resourceNo].materialMap[key].demandNum += demandNum;
                  supplierMap[supplierName].resourceMap[resourceNo].totalQty += demandNum;
                  supplierMap[supplierName].totalQty += demandNum;
                });
              });
              
              var result = [];
              var serialNumber = 1;
              
              Object.keys(supplierMap).forEach(function(supplierName) {
                var supplierData = supplierMap[supplierName];
                var resourceNos = Object.keys(supplierData.resourceMap);
                
                var isFirstResource = true;
                resourceNos.forEach(function(resourceNo) {
                  var resourceData = supplierData.resourceMap[resourceNo];
                  var invCode = resourceData.invCode;
                  var materialKeys = Object.keys(resourceData.materialMap);
                  
                  var materials = materialKeys.map(function(key) {
                    return resourceData.materialMap[key];
                  });
                  
                  materials.sort(function(a, b) {
                    var composeA = a.composeMaterialNo || "";
                    var composeB = b.composeMaterialNo || "";
                    
                    if (composeA && composeB) {
                      var cmp = composeA.localeCompare(composeB, "en");
                      if (cmp !== 0) return cmp;
                    }
                    
                    if (composeA && !composeB) return -1;
                    if (!composeA && composeB) return 1;
                    
                    var cmp = a.materialNo.localeCompare(b.materialNo, "en");
                    return cmp === 0 ? 0 : -cmp;
                  });
                  
                  materials.forEach(function(materialData, matIndex) {
                    var packageQty = materialData.packageNum;
                    var demandQty = materialData.demandNum;
                    var cartonPack = formatPackagePack(packageQty, demandQty, "箱");
                    var gluePack = formatPackagePack(materialData.minPackageNum, demandQty, "胶箱");
                    
                    result.push({
                      serialNumber: serialNumber,
                      supplierName: isFirstResource && matIndex === 0 ? supplierName : "",
                      manufacturerName: isFirstResource && matIndex === 0 ? supplierData.manufacturerName : "",
                      invCode: invCode,
                      resourceNo: resourceNo,
                      materialNo: materialData.materialNo,
                      materialDesc: materialData.materialDesc,
                      demandNum: formatDemandNum(demandQty),
                      cartonPack: cartonPack,
                      gluePack: gluePack,
                      isGroupStart: matIndex === 0,
                      composeMaterialNo: materialData.composeMaterialNo,
                    });
                    serialNumber++;
                  });
                  
                  result.push({
                    serialNumber: "", supplierName: "", manufacturerName: "", invCode: invCode, resourceNo: resourceNo, materialNo: "", materialDesc: invCode + " 计数",
                    demandNum: "", cartonPack: "", gluePack: "",
                    isInvTotal: true,
                  });
                  isFirstResource = false;
                });
                result.push({
                  serialNumber: "", supplierName: "", manufacturerName: "", invCode: "", resourceNo: "", materialNo: "", materialDesc: supplierName + " 总计",
                  demandNum: supplierData.materialCount, cartonPack: "", gluePack: "",
                  isSupplierTotal: true,
                });
              });
              
              return result;
            }
            
            function openNonPlsSummaryPrint() {
              var summaryData = processNonPlsSummaryData(originalData);
              var printWindow = window.open("", "_blank");
              var now = new Date();
              var printTime = now.getFullYear() + "/" + String(now.getMonth() + 1).padStart(2, "0") + "/" + String(now.getDate()).padStart(2, "0") + " " + String(now.getHours()).padStart(2, "0") + ":" + String(now.getMinutes()).padStart(2, "0");
              
              var firstItem = summaryData.find(function(item) { return item.supplierName && item.supplierName !== ''; });
              var supplierName = firstItem ? firstItem.supplierName : '';
              var manufacturerName = firstItem ? firstItem.manufacturerName : '';
              
              var html = "<!DOCTYPE html><html><head><meta charset='UTF-8'><title>出库汇总打印预览</title><style>*{box-sizing:border-box;margin:0;padding:0;}@media print{@page{size:A4 landscape;margin:15mm;}body{font-family:'Microsoft YaHei',Arial,sans-serif;font-size:10px;}}body{font-family:'Microsoft YaHei',Arial,sans-serif;font-size:12px;padding:20px;}.summary-container{max-width:100%;margin:0 auto;}.print-header{margin-bottom:15px;padding-bottom:10px;border-bottom:2px solid #333;}.header-title{font-size:20px;font-weight:bold;color:#333;margin-bottom:10px;text-align:center;}.header-row{display:flex;justify-content:space-between;margin-bottom:8px;font-size:14px;}.header-left{flex:1;text-align:left;font-weight:bold;}.header-right{flex:1;text-align:right;color:#666;}.summary-table{width:100%;border-collapse:collapse;margin-top:10px;}.summary-table th,.summary-table td{border:1px solid #333;padding:6px 4px;text-align:center;vertical-align:middle;font-size:11px;word-break:break-all;}.summary-table th{background:#f5f5f5;font-weight:bold;font-size:12px;}.inv-group{background:#f0f8ff;}.total-row{background:#fff3cd;font-weight:bold;}.col-serial{width:6%;}.col-material-no{width:18%;}.col-qty{width:10%;}.col-desc{width:22%;}.col-inv{width:10%;}.col-carton{width:15%;font-size:13px;font-weight:bold;padding:8px 4px;}.col-glue{width:15%;font-size:13px;font-weight:bold;padding:8px 4px;}.print-footer{text-align:center;margin-top:20px;padding-top:10px;border-top:1px solid #ccc;font-size:10px;color:#999;}.copy-toolbar{text-align:right;margin-bottom:10px;}.copy-btn{padding:6px 16px;border:1px solid #1890ff;border-radius:4px;background:white;color:#1890ff;font-size:12px;cursor:pointer;}.copy-btn:hover{background:#1890ff;color:white;}</style></head><body><div class='summary-container'><div class='print-header'><div class='header-title'>出库单非PLS汇总表</div><div class='header-row'><div class='header-left'>供应商：" + supplierName + " | 主机厂：" + manufacturerName + "</div><div class='header-right'>打印时间：" + printTime + "</div></div></div><div class='copy-toolbar'><button class='copy-btn' onclick='copySummaryToExcel()'>复制到Excel</button></div><table class='summary-table'><thead><tr><th class='col-serial'>序号</th><th class='col-material-no'>物料编码</th><th class='col-qty'>汇总数量</th><th class='col-desc'>物料描述</th><th class='col-inv'>子库</th><th class='col-carton'>纸箱包装</th><th class='col-glue'>胶框包装</th></tr></thead><tbody>";
              
              summaryData.forEach(function(item) {
                if (item.isSupplierTotal) {
                  html += "<tr class='total-row'><td colspan='3' style='text-align:left;padding-left:40px;'>" + item.materialDesc + "</td><td>" + item.demandNum + "</td><td></td><td></td><td></td></tr>";
                } else if (item.isInvTotal) {
                  html += "<tr class='total-row' style='background:#e6f7ff;'><td></td><td></td><td></td><td>" + item.materialDesc + "</td><td>" + item.invCode + "</td><td></td><td></td></tr>";
                } else {
                  var rowClass = item.isGroupStart ? "inv-group" : "material-row";
                  var materialNo = item.composeMaterialNo ? item.composeMaterialNo : item.materialNo;
              var materialDesc = item.composeMaterialNo ? (item.materialDesc + " (" + item.materialNo + ")") : item.materialDesc;
              html += "<tr class='" + rowClass + "'><td class='col-serial'>" + (item.serialNumber || "") + "</td><td class='col-material-no'>" + (materialNo || "") + "</td><td class='col-qty'>" + (item.demandNum || "") + "</td><td class='col-desc' style='text-align:left;'>" + (materialDesc || "") + "</td><td class='col-inv'>" + (item.invCode || "") + "</td><td class='col-carton'>" + (item.cartonPack || "") + "</td><td class='col-glue'>" + (item.gluePack || "") + "</td></tr>";
                }
              });
              
              html += "</tbody></table><div class='print-footer'>打印时间：" + printTime + " | 出库单非PLS汇总表</div></div></body></html>";
              
              printWindow.document.write(html);
              printWindow.document.close();
              printWindow.focus();
              
              var copyFn = "function copySummaryToExcel(){var tb=document.querySelector('.summary-table tbody');if(!tb)return;var th=document.querySelector('.summary-table thead');var L=[],T=String.fromCharCode(9),N=String.fromCharCode(10);var hl=document.querySelector('.header-row .header-left');var hr=document.querySelector('.header-row .header-right');var hVals=['','','','','','',''];if(hl){var hparts=hl.textContent.trim().split('|');var supplier='',manufacturer='';for(var hpi=0;hpi<hparts.length;hpi++){var kv=hparts[hpi].trim().split('：');if(hparts[hpi].trim().indexOf('供应商')!==-1){supplier=kv[kv.length-1].trim()}else if(hparts[hpi].trim().indexOf('主机厂')!==-1){manufacturer=kv[kv.length-1].trim()}}hVals[0]=manufacturer;hVals[1]=supplier}if(hr){var kv=hr.textContent.trim().split('：');hVals[5]=kv[kv.length-1].trim()}L.push(hVals.join(T));if(th){var hc=th.querySelectorAll('tr th'),h=[];hc.forEach(function(c){h.push(c.textContent.trim())});L.push(h.join(T))}var rw=tb.querySelectorAll('tr');rw.forEach(function(r){var cl=r.querySelectorAll('td'),rd=[];cl.forEach(function(c){rd.push(c.textContent.trim())});L.push(rd.join(T))});var tx=L.join(N);var btn=document.querySelector('.copy-btn');if(btn)btn.textContent='\u2713';if(!navigator.clipboard){var ta=document.createElement('textarea');ta.value=tx;document.body.appendChild(ta);ta.select();document.execCommand('copy');document.body.removeChild(ta);if(btn)setTimeout(function(){btn.textContent='\u590D\u5236\u5230Excel'},1500);return}navigator.clipboard.writeText(tx).then(function(){if(btn)setTimeout(function(){btn.textContent='\u590D\u5236\u5230Excel'},1500)})['catch'](function(){var ta=document.createElement('textarea');ta.value=tx;document.body.appendChild(ta);ta.select();document.execCommand('copy');document.body.removeChild(ta);if(btn)setTimeout(function(){btn.textContent='\u590D\u5236\u5230Excel'},1500)})}";
              var cs = printWindow.document.createElement("script");
              cs.textContent = copyFn;
              printWindow.document.body.appendChild(cs);
              
              setTimeout(function() { printWindow.print(); }, 250);
            }
          </` +
        `script>
        </body>
        </html>
      `;

      printWindow.document.write(summaryHtml);
      printWindow.document.close();
      printWindow.focus();

      setTimeout(async () => {
        try {
          await renderLabelQrCodes(printWindow);
        } catch (e) {
          console.error("二维码生成失败:", e);
        }
      }, 100);
    };

    const openPrintPreviewSummary = (summaryData) => {
      const printWindow = window.open("", "_blank");
      const now = new Date();
      const printTime = `${(now.getMonth() + 1)
        .toString()
        .padStart(2, "0")}/${now.getDate().toString().padStart(2, "0")} ${now
        .getHours()
        .toString()
        .padStart(2, "0")}:${now.getMinutes().toString().padStart(2, "0")}`;

      let html = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8">
          <title>出库汇总打印预览</title>
          <style>
            * {
              box-sizing: border-box;
              margin: 0;
              padding: 0;
            }
            @media print {
              @page {
                size: A4 landscape;
                margin: 15mm;
              }
              body {
                font-family: "Microsoft YaHei", Arial, sans-serif;
                font-size: 10px;
              }
              .print-header {
                position: fixed;
                top: 0;
                left: 0;
                right: 0;
              }
              .print-footer {
                position: fixed;
                bottom: 0;
                left: 0;
                right: 0;
              }
            }
            body {
              font-family: "Microsoft YaHei", Arial, sans-serif;
              font-size: 12px;
              padding: 20px;
            }
            .summary-container {
              max-width: 100%;
              margin: 0 auto;
            }
            .print-header {
              text-align: center;
              margin-bottom: 20px;
              padding-bottom: 10px;
              border-bottom: 2px solid #333;
            }
            .header-title {
              font-size: 20px;
              font-weight: bold;
              color: #333;
              margin-bottom: 8px;
            }
            .header-info {
              font-size: 12px;
              color: #666;
            }
            .summary-table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 10px;
            }
            .summary-table th,
            .summary-table td {
              border: 1px solid #333;
              padding: 8px 6px;
              text-align: center;
              vertical-align: middle;
              font-size: 11px;
              word-break: break-all;
            }
            .summary-table th {
              background: #f5f5f5;
              font-weight: bold;
              font-size: 12px;
            }
            .supplier-group {
              background: #e8f4fc;
              font-weight: bold;
            }
            .supplier-group td {
              padding-top: 12px;
              padding-bottom: 12px;
            }
            .inv-group {
              background: #f0f8ff;
            }
            .total-row {
              background: #fff3cd;
              font-weight: bold;
            }
            .total-row td {
              padding-top: 10px;
              padding-bottom: 10px;
            }
            .material-row:hover {
              background: #f9f9f9;
            }
            .col-supplier {
              width: 15%;
            }
            .col-inv {
              width: 10%;
            }
            .col-material-no {
              width: 18%;
            }
            .col-desc {
              width: 22%;
            }
            .col-qty {
              width: 10%;
            }
            .col-carton {
              width: 15%;
            }
            .col-glue {
              width: 15%;
            }
            .print-footer {
              text-align: center;
              margin-top: 20px;
              padding-top: 10px;
              border-top: 1px solid #ccc;
              font-size: 10px;
              color: #999;
            }
            .page-break {
              page-break-after: always;
            }
            .copy-toolbar {
              text-align: right;
              margin-bottom: 10px;
            }
            .copy-btn {
              padding: 6px 16px;
              border: 1px solid #1890ff;
              border-radius: 4px;
              background: white;
              color: #1890ff;
              font-size: 12px;
              cursor: pointer;
              transition: all 0.2s;
            }
            .copy-btn:hover {
              background: #1890ff;
              color: white;
            }
          </style>
        </head>
        <body>
          <div class="summary-container">
            <div class="print-header">
              <div class="header-title">出库单汇总表</div>
              <div class="header-info">打印时间：${printTime} | 共 ${summaryData.length} 条记录</div>
            </div>


            <div class="copy-toolbar">
              <button class="copy-btn" onclick="copySummaryToExcel()">复制到Excel</button>
            </div>

            <table class="summary-table">
              <thead>
                <tr>
                  <th class="col-supplier">供应商</th>
                  <th class="col-inv">子库</th>
                  <th class="col-material-no">物料编码</th>
                  <th class="col-desc">物料描述</th>
                  <th class="col-qty">需求数量</th>
                  <th class="col-carton">纸箱包装</th>
                  <th class="col-glue">胶框包装</th>
                </tr>
              </thead>
              <tbody>
      `;

      summaryData.forEach((item) => {
        if (item.isSupplierTotal) {
          html += `
            <tr class="total-row">
              <td colspan="4" style="text-align: left; padding-left: 20px;">${item.materialDesc}</td>
              <td style="font-size: 14px;">${item.demandNum}</td>
              <td></td>
              <td></td>
            </tr>
          `;
        } else if (item.isInvTotal) {
          html += `
            <tr class="total-row" style="background: #e6f7ff;">
              <td colspan="4" style="text-align: left; padding-left: 40px;">${item.materialDesc}</td>
              <td>${item.demandNum}</td>
              <td></td>
              <td></td>
            </tr>
          `;
        } else {
          const rowClass = item.isGroupStart ? "inv-group" : "material-row";
          html += `
            <tr class="${rowClass}">
              <td class="col-supplier">${item.supplierName || ""}</td>
              <td class="col-inv">${item.invCode || ""}</td>
              <td class="col-material-no">${item.materialNo || ""}</td>
              <td class="col-desc" style="text-align: left;">${
                item.materialDesc || ""
              }</td>
              <td class="col-qty">${item.demandNum || ""}</td>
              <td class="col-carton">${item.cartonPack || ""}</td>
              <td class="col-glue">${item.gluePack || ""}</td>
            </tr>
          `;
        }
      });

      html +=
        `
              </tbody>
            </table>

            <div class="print-footer">
              打印时间：${printTime} | 出库单汇总表
            </div>
          </div>
          <script>
          function copySummaryToExcel() {
            var tbody = document.querySelector(".summary-table tbody");
            if (!tbody) return;
            var thead = document.querySelector(".summary-table thead");
            var lines = [];
            var hl = document.querySelector(".header-row .header-left");
            var hr = document.querySelector(".header-row .header-right");
            var hl = document.querySelector(".header-row .header-left");
            var hr = document.querySelector(".header-row .header-right");
            var hVals = [];
            if (hl) {
              var hparts = hl.textContent.trim().split("|");
              for (var hpi = 0; hpi < hparts.length; hpi++) {
                var kv = hparts[hpi].trim().split("：");
                hVals.push(kv[kv.length - 1].trim());
              }
            }
            if (hr) {
              var kv = hr.textContent.trim().split("：");
              hVals.push(kv[kv.length - 1].trim());
            }
            lines.push(hVals.join("\t"));
              var header = [];
              headerCells.forEach(function(th) { header.push(th.textContent.trim()); });
              lines.push(header.join("\t"));
            }
            var rows = tbody.querySelectorAll("tr");
            rows.forEach(function(row) {
              var cells = row.querySelectorAll("td");
              var rowData = [];
              cells.forEach(function(cell) { rowData.push(cell.textContent.trim()); });
              lines.push(rowData.join("\t"));
            });
            var text = lines.join("\n");
            var btn = document.querySelector(".copy-btn");
            if (btn) btn.textContent = "\u2713";
            if (!navigator.clipboard) {
              var ta = document.createElement("textarea");
              ta.value = text;
              document.body.appendChild(ta);
              ta.select();
              document.execCommand("copy");
              document.body.removeChild(ta);
              if (btn) setTimeout(function() { btn.textContent = "\u590D\u5236\u5230Excel"; }, 1500);
              return;
            }
            navigator.clipboard.writeText(text).then(function() {
              if (btn) setTimeout(function() { btn.textContent = "\u590D\u5236\u5230Excel"; }, 1500);
            }).catch(function() {
              var ta = document.createElement("textarea");
              ta.value = text;
              document.body.appendChild(ta);
              ta.select();
              document.execCommand("copy");
              document.body.removeChild(ta);
              if (btn) setTimeout(function() { btn.textContent = "\u590D\u5236\u5230Excel"; }, 1500);
            });
          }
          </` +
        `script>
        </body>
        </html>
      `;

      printWindow.document.write(html);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
      }, 250);
    };

    const batchSize = 250;

    const batchUpdateIsPrint = async (ids) => {
      const batches = [];
      for (let i = 0; i < ids.length; i += batchSize) {
        batches.push(ids.slice(i, i + batchSize));
      }
      for (const batch of batches) {
        await updateIsPrint(batch.join(","));
      }
    };

    const batchGetPrintData = async (ids) => {
      // 出库单 id 保持字符串，避免大整数精度丢失
      const idStrs = (ids || []).map((id) => toIdStr(id)).filter(Boolean);
      const batches = [];
      for (let i = 0; i < idStrs.length; i += batchSize) {
        batches.push(idStrs.slice(i, i + batchSize));
      }
      const allData = [];
      for (const batch of batches) {
        const res = await getResourceAndNoPlsNo(
          { ids: batch.join(",") },
          { timeout: 300000 },
        );
        if (res && res.success && res.data) {
          allData.push(...res.data);
        } else if (res && res.data && res.data.success && res.data.data) {
          allData.push(...res.data.data);
        }
      }
      return allData;
    };

    const previewPrint = () => {
      if (!selectedData.value || selectedData.value.length === 0) {
        toast.error("请选择需要打印的出库单");
        return;
      }

      const ids = selectedData.value.map((item) => toIdStr(item.id));
      loading.value = true;

      batchGetPrintData(ids)
        .then((printData) => {
          if (printData && printData.length > 0) {
            const summaryData = processSummaryData(printData);
            openPrintPreviewSummary(summaryData);
          } else {
            toast.error("获取打印数据失败或无数据可打印");
          }
        })
        .catch((err) => {
          console.error("打印数据获取异常:", err);
          toast.error("获取打印数据失败");
        })
        .finally(() => {
          loading.value = false;
        });
    };

    const previewNonPlsSummary = () => {
      if (!selectedData.value || selectedData.value.length === 0) {
        toast.error("请选择需要打印的出库单");
        return;
      }

      const ids = selectedData.value.map((item) => toIdStr(item.id));
      loading.value = true;

      batchGetPrintData(ids)
        .then((printData) => {
          if (printData && printData.length > 0) {
            const summaryData = processNonPlsSummaryData(printData);
            openPrintPreviewSummary(summaryData);
          } else {
            toast.error("获取打印数据失败或无数据可打印");
          }
        })
        .catch((err) => {
          console.error("打印数据获取异常:", err);
          toast.error("获取打印数据失败");
        })
        .finally(() => {
          loading.value = false;
        });
    };

    const batchPrint = () => {
      if (!selectedData.value || selectedData.value.length === 0) {
        toast.error("请选择需要打印的出库单");
        return;
      }

      const invCodes = [
        ...new Set(
          selectedData.value.map((item) => item.invCode).filter(Boolean),
        ),
      ];

      const paperSizes = [
        { width: 100, height: 90, label: "100mm × 90mm" },
        { width: 100, height: 80, label: "100mm × 80mm" },
        { width: 100, height: 75, label: "100mm × 75mm" },
        { width: 100, height: 70, label: "100mm × 70mm" },
      ];

      const selectedInvCodes = [];

      const selectPaperSize = () => {
        const container = document.createElement("div");
        container.style.cssText = `
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0,0,0,0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 9999;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
        `;

        const modal = document.createElement("div");
        modal.style.cssText = `
          background: white;
          border-radius: 12px;
          padding: 28px;
          width: 360px;
          box-shadow: 0 10px 40px rgba(0,0,0,0.15);
        `;

        const title = document.createElement("h3");
        title.textContent = "选择纸张大小";
        title.style.cssText = `
          margin: 0 0 20px 0;
          font-size: 18px;
          font-weight: 600;
          text-align: center;
          color: #1f2937;
        `;

        modal.appendChild(title);

        const customLabel = document.createElement("div");
        customLabel.style.cssText = `
          margin-bottom: 20px;
        `;

        const labelText = document.createElement("label");
        labelText.textContent = "备注信息";
        labelText.style.cssText = `
          display: block;
          font-size: 14px;
          color: #4b5563;
          margin-bottom: 8px;
          font-weight: 500;
        `;
        customLabel.appendChild(labelText);

        const input = document.createElement("input");
        input.type = "text";
        input.placeholder = "输入备注信息";
        input.style.cssText = `
          width: 100%;
          padding: 10px 14px;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          font-size: 14px;
          box-sizing: border-box;
          transition: border-color 0.2s, box-shadow 0.2s;
        `;
        input.onfocus = () => {
          input.style.borderColor = "#3b82f6";
          input.style.boxShadow = "0 0 0 3px rgba(59, 130, 246, 0.1)";
        };
        input.onblur = () => {
          input.style.borderColor = "#d1d5db";
          input.style.boxShadow = "none";
        };
        customLabel.appendChild(input);
        modal.appendChild(customLabel);

        if (invCodes.length > 0) {
          const invSection = document.createElement("div");
          invSection.style.cssText = `
            margin-bottom: 20px;
            padding-top: 16px;
            border-top: 1px solid #e5e7eb;
          `;

          const invTitle = document.createElement("label");
          invTitle.textContent = "子库拆包（选填）";
          invTitle.style.cssText = `
            display: block;
            font-size: 14px;
            color: #4b5563;
            margin-bottom: 12px;
            font-weight: 500;
          `;
          invSection.appendChild(invTitle);

          const invButtons = document.createElement("div");
          invButtons.style.cssText = `
            display: flex;
            gap: 10px;
            margin-bottom: 12px;
          `;

          const selectAllBtn = document.createElement("button");
          selectAllBtn.textContent = "全选";
          selectAllBtn.style.cssText = `
            padding: 6px 12px;
            border: 1px solid #3b82f6;
            border-radius: 4px;
            background: white;
            font-size: 12px;
            cursor: pointer;
            color: #3b82f6;
            transition: all 0.2s;
          `;
          selectAllBtn.onclick = () => {
            const checkboxes = invSection.querySelectorAll(
              'input[type="checkbox"]',
            );
            checkboxes.forEach((cb) => {
              cb.checked = true;
            });
            selectedInvCodes.length = 0;
            invCodes.forEach((code) => selectedInvCodes.push(code));
          };
          invButtons.appendChild(selectAllBtn);

          const clearAllBtn = document.createElement("button");
          clearAllBtn.textContent = "清空";
          clearAllBtn.style.cssText = `
            padding: 6px 12px;
            border: 1px solid #d1d5db;
            border-radius: 4px;
            background: white;
            font-size: 12px;
            cursor: pointer;
            color: #6b7280;
            transition: all 0.2s;
          `;
          clearAllBtn.onclick = () => {
            const checkboxes = invSection.querySelectorAll(
              'input[type="checkbox"]',
            );
            checkboxes.forEach((cb) => {
              cb.checked = false;
            });
            selectedInvCodes.length = 0;
          };
          invButtons.appendChild(clearAllBtn);
          invSection.appendChild(invButtons);

          const invList = document.createElement("div");
          invList.style.cssText = `
            display: flex;
            flex-wrap: wrap;
            gap: 10px;
          `;

          invCodes.forEach((invCode) => {
            const item = document.createElement("label");
            item.style.cssText = `
              display: flex;
              align-items: center;
              cursor: pointer;
              padding: 8px 12px;
              background: #f9fafb;
              border-radius: 6px;
              transition: background 0.2s;
            `;
            item.onmouseenter = () => {
              item.style.background = "#f3f4f6";
            };
            item.onmouseleave = () => {
              item.style.background = "#f9fafb";
            };

            const checkbox = document.createElement("input");
            checkbox.type = "checkbox";
            checkbox.value = invCode;
            checkbox.style.cssText = `
              width: 16px;
              height: 16px;
              margin-right: 8px;
              cursor: pointer;
            `;
            checkbox.onchange = (e) => {
              const idx = selectedInvCodes.indexOf(invCode);
              if (e.target.checked && idx === -1) {
                selectedInvCodes.push(invCode);
              } else if (!e.target.checked && idx > -1) {
                selectedInvCodes.splice(idx, 1);
              }
            };

            const text = document.createElement("span");
            text.textContent = invCode;
            text.style.cssText = `
              font-size: 13px;
              color: #374151;
            `;

            item.appendChild(checkbox);
            item.appendChild(text);
            invList.appendChild(item);
          });

          invSection.appendChild(invList);
          modal.appendChild(invSection);
        }

        const buttonGroup = document.createElement("div");
        buttonGroup.style.cssText = `
          display: flex;
          flex-direction: column;
          gap: 10px;
        `;

        paperSizes.forEach((size) => {
          const button = document.createElement("button");
          button.textContent = size.label;
          button.style.cssText = `
            padding: 14px;
            border: 1px solid #e5e7eb;
            border-radius: 8px;
            background: white;
            font-size: 15px;
            cursor: pointer;
            transition: all 0.2s;
            color: #374151;
            font-weight: 500;
          `;
          button.onmouseenter = () => {
            button.style.background = "#f9fafb";
            button.style.borderColor = "#3b82f6";
          };
          button.onmouseleave = () => {
            button.style.background = "white";
            button.style.borderColor = "#e5e7eb";
          };
          button.onclick = () => {
            document.body.removeChild(container);
            handlePrintConfirm({
              ...size,
              customText: input.value.trim(),
              splitInvCodes: selectedInvCodes,
            });
          };
          buttonGroup.appendChild(button);
        });

        modal.appendChild(buttonGroup);

        const cancelBtn = document.createElement("button");
        cancelBtn.textContent = "取消";
        cancelBtn.style.cssText = `
          width: 100%;
          padding: 12px;
          border: none;
          border-radius: 8px;
          background: #f3f4f6;
          font-size: 14px;
          cursor: pointer;
          margin-top: 12px;
          color: #6b7280;
          transition: background 0.2s;
        `;
        cancelBtn.onmouseenter = () => {
          cancelBtn.style.background = "#e5e7eb";
        };
        cancelBtn.onmouseleave = () => {
          cancelBtn.style.background = "#f3f4f6";
        };
        cancelBtn.onclick = () => {
          document.body.removeChild(container);
        };
        modal.appendChild(cancelBtn);

        container.appendChild(modal);
        document.body.appendChild(container);
      };

      selectPaperSize();
    };

    const handlePrintConfirm = (paperSize) => {
      const confirmContainer = document.createElement("div");
      confirmContainer.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0,0,0,0.5);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 9999;
      `;

      const confirmModal = document.createElement("div");
      confirmModal.style.cssText = `
        background: white;
        border-radius: 8px;
        padding: 24px;
        width: 320px;
        box-shadow: 0 4px 20px rgba(0,0,0,0.2);
      `;

      const confirmTitle = document.createElement("h3");
      confirmTitle.textContent = "确认打印";
      confirmTitle.style.cssText = `
        margin: 0 0 16px 0;
        font-size: 16px;
        font-weight: bold;
        text-align: center;
      `;
      confirmModal.appendChild(confirmTitle);

      const confirmText = document.createElement("p");
      confirmText.textContent = `确定要打印选中的 ${selectedData.value.length} 条出库单吗？`;
      confirmText.style.cssText = `
        margin: 0 0 20px 0;
        font-size: 14px;
        color: #666;
        text-align: center;
        line-height: 1.5;
      `;
      confirmModal.appendChild(confirmText);

      const buttonContainer = document.createElement("div");
      buttonContainer.style.cssText = `
        display: flex;
        gap: 12px;
      `;

      const cancelBtn = document.createElement("button");
      cancelBtn.textContent = "取消";
      cancelBtn.style.cssText = `
        flex: 1;
        padding: 10px;
        border: 1px solid #ddd;
        border-radius: 4px;
        background: white;
        font-size: 14px;
        cursor: pointer;
        transition: all 0.2s;
      `;
      cancelBtn.onmouseenter = () => {
        cancelBtn.style.background = "#f5f5f5";
      };
      cancelBtn.onmouseleave = () => {
        cancelBtn.style.background = "white";
      };
      cancelBtn.onclick = () => {
        document.body.removeChild(confirmContainer);
      };
      buttonContainer.appendChild(cancelBtn);

      const confirmBtn = document.createElement("button");
      confirmBtn.textContent = "确定";
      confirmBtn.style.cssText = `
        flex: 1;
        padding: 10px;
        border: none;
        border-radius: 4px;
        background: linear-gradient(135deg, #1890ff 0%, #096dd9 100%);
        color: white;
        font-size: 14px;
        cursor: pointer;
        transition: all 0.2s;
      `;
      confirmBtn.onmouseenter = () => {
        confirmBtn.style.transform = "translateY(-2px)";
        confirmBtn.style.boxShadow = "0 4px 12px rgba(24, 144, 255, 0.4)";
      };
      confirmBtn.onmouseleave = () => {
        confirmBtn.style.transform = "translateY(0)";
        confirmBtn.style.boxShadow = "none";
      };
      confirmBtn.onclick = async () => {
        document.body.removeChild(confirmContainer);
        const ids = selectedData.value.map((item) => toIdStr(item.id));

        try {
          const originalData = await batchGetPrintData(ids);
          if (originalData && originalData.length > 0) {
            const printData = processPrintData(originalData);
            openPrintPreview(
              printData,
              paperSize,
              originalData,
              paperSize.splitInvCodes || [],
              ids,
            );
            const selectedIds = [...ids];
            await fetchPage();
            setTimeout(() => {
              selectedData.value = [];
              if (tableRef.value) {
                tableRef.value.clearSelection();
                list.value.forEach((row) => {
                  if (selectedIds.includes(row.id)) {
                    tableRef.value.toggleRowSelection(row, true);
                    selectedData.value.push(row);
                  }
                });
              }
            }, 100);
          } else {
            toast.error("获取打印数据失败或无数据可打印");
          }
        } catch (err) {
          console.error("批量打印异常:", err);
          toast.error("批量打印失败");
        }
      };
      buttonContainer.appendChild(confirmBtn);

      confirmModal.appendChild(buttonContainer);
      confirmContainer.appendChild(confirmModal);
      document.body.appendChild(confirmContainer);
    };

    // TCL月度标签打印预览（独立模板，不影响现有PLS/非PLS标签）
    const openTclMonthlyPrintPreview = (printData, ids = []) => {
      const width = 100;
      const height = 75;
      const monthNum = new Date().getMonth() + 1;

      const escapeHtml = (str) =>
        String(str ?? "")
          .replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;")
          .replace(/"/g, "&quot;");

      const printWindow = window.open("", "_blank");
      if (!printWindow) {
        toast.error("无法打开打印窗口，请检查浏览器弹窗拦截");
        return;
      }

      let html = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8">
          <title>TCL月度标签打印预览</title>
          <style>
            * { box-sizing: border-box; margin: 0; padding: 0; }
            @page {
              size: ${width}mm ${height}mm;
              margin: 0;
            }
            @media print {
              html, body {
                margin: 0 !important;
                padding: 0 !important;
                width: ${width}mm;
                height: auto;
                background: #fff !important;
              }
              .print-toolbar { display: none !important; }
              .print-content {
                margin: 0 !important;
                padding: 0 !important;
                background: transparent !important;
              }
              .tcl-label {
                page-break-after: always !important;
                break-after: page !important;
                page-break-inside: avoid !important;
                margin: 0 !important;
                border: 1px solid #000 !important;
                box-shadow: none !important;
                width: ${width}mm !important;
                height: ${height}mm !important;
                background: #fff !important;
              }
              .tcl-body {
                background: #fff !important;
              }
              .tcl-month-bar {
                background: #fff !important;
              }
              .tcl-month-num,
              .tcl-month-char {
                color: #111 !important;
              }
              .tcl-side-line {
                background: none !important;
                border-left: 1.5px solid #000 !important;
              }
            }
            html, body {
              margin: 0;
              padding: 0;
              font-family: "Microsoft YaHei", "PingFang SC", "SimHei", Arial, sans-serif;
              background: #eceff3;
            }
            body { width: ${width}mm; }
            .print-toolbar {
              position: fixed;
              top: 0;
              left: 0;
              right: 0;
              background: #333;
              padding: 10px 20px;
              display: flex;
              justify-content: flex-end;
              gap: 12px;
              z-index: 1000;
            }
            .toolbar-btn {
              padding: 8px 20px;
              border: none;
              border-radius: 4px;
              font-size: 14px;
              cursor: pointer;
              transition: all 0.2s;
            }
            .toolbar-btn.primary {
              background: #1890ff;
              color: white;
            }
            .toolbar-btn.primary:hover {
              background: #096dd9;
            }
            .toolbar-btn.secondary {
              background: #fff;
              color: #333;
            }
            .toolbar-btn.secondary:hover {
              background: #e6e6e6;
            }
            .print-content { margin-top: 56px; padding: 12px 0 20px; }
            .tcl-label {
              width: ${width}mm;
              height: ${height}mm;
              margin: 0 auto 14px;
              display: flex;
              flex-direction: row;
              overflow: hidden;
              background: #fff;
              border: 1px solid #222;
              box-shadow: 0 4px 14px rgba(0,0,0,0.12);
              page-break-after: always;
            }
            .tcl-month-bar {
              width: 14mm;
              flex-shrink: 0;
              background: #fff;
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              gap: 1.5mm;
            }
            .tcl-side-line {
              width: 0;
              flex-shrink: 0;
              align-self: stretch;
              background: none;
              border-left: 1.5px solid #000;
            }
            .tcl-month-num,
            .tcl-month-char {
              color: #111;
              font-weight: 900;
              line-height: 1;
            }
            .tcl-month-num {
              font-size: 32px;
            }
            .tcl-month-char {
              font-size: 28px;
            }
            .tcl-body {
              flex: 1;
              min-width: 0;
              height: 100%;
              padding: 2.5mm 3.2mm 2.2mm 2.8mm;
              display: flex;
              flex-direction: column;
              justify-content: space-between;
              color: #111;
              background: #fff;
            }
            .tcl-top,
            .tcl-mid,
            .tcl-bottom {
              width: 100%;
            }
            .tcl-pill {
              display: inline-flex;
              align-items: center;
              justify-content: center;
              align-self: flex-start;
              border: 1.6px solid #111;
              border-radius: 999px;
              padding: 0.8mm 3mm;
              font-size: 13px;
              font-weight: 700;
              line-height: 1.15;
              max-width: 100%;
              background: transparent;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
            }
            .tcl-company {
              margin-top: 1.6mm;
              font-size: 13px;
              font-weight: 600;
              line-height: 1.2;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
            }
            .tcl-material-no {
              margin-top: 1.4mm;
              font-size: 24px;
              font-weight: 900;
              letter-spacing: 0.4px;
              line-height: 1.05;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
            }
            .tcl-material-desc {
              margin-top: 1.2mm;
              font-size: 11.5px;
              line-height: 1.28;
              font-weight: 500;
              max-height: 11mm;
              overflow: hidden;
              word-break: break-all;
            }
            .tcl-total {
              margin-top: 1.8mm;
              font-size: 15px;
              font-weight: 700;
              line-height: 1;
              display: flex;
              align-items: baseline;
              gap: 1.5mm;
            }
            .tcl-total strong {
              font-size: 28px;
              font-weight: 900;
            }
            .tcl-divider {
              margin: 1.8mm 0 1.6mm;
              border: none;
              border-top: 1.5px solid #111;
            }
            .tcl-pack {
              font-size: 16px;
              font-weight: 800;
              line-height: 1.15;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
            }
            .tcl-order {
              margin-top: 2mm;
            }
            .tcl-order .tcl-pill {
              font-size: 12px;
              font-weight: 700;
              max-width: 100%;
              padding: 0.9mm 2.8mm;
            }
            .tcl-factory {
              margin-top: 2.2mm;
              font-size: 14px;
              font-weight: 700;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
            }
            .tcl-location {
              margin-top: 1.2mm;
              font-size: 11.5px;
              font-weight: 600;
              line-height: 1.25;
              word-break: break-all;
              max-height: 8.5mm;
              overflow: hidden;
            }
            .tcl-location-label {
              font-weight: 800;
            }
          </style>
        </head>
        <body style="margin: 0; padding: 0;">
          <div class="print-toolbar">
            <button class="toolbar-btn secondary" onclick="handlePrintClick()">打印标签</button>
            <button class="toolbar-btn primary" onclick="window.close()">关闭</button>
          </div>
          <div class="print-content">
      `;

      printData.forEach((item) => {
        const orderNo = item.orderRemark || "";
        const headerCode = item.invCode || "";
        html += `
          <div class="tcl-label">
            <div class="tcl-month-bar">
              <div class="tcl-month-num">${monthNum}</div>
              <div class="tcl-month-char">月</div>
            </div>
            <div class="tcl-side-line"></div>
            <div class="tcl-body">
              <div class="tcl-top">
                <div class="tcl-pill">${escapeHtml(headerCode)}</div>
                <div class="tcl-company">${escapeHtml(item.supplierName || "")}</div>
                <div class="tcl-material-no">${escapeHtml(item.materialNo || "")}</div>
                <div class="tcl-material-desc">${escapeHtml(item.materialDesc || "")}</div>
                <div class="tcl-total">总数:<strong>${escapeHtml(item.demandNum ?? "")}</strong></div>
              </div>
              <div class="tcl-mid">
                <hr class="tcl-divider" />
                <div class="tcl-pack">${escapeHtml(item.tclCartonPack || "")}</div>
                <div class="tcl-order">
                  <div class="tcl-pill">订单号：${escapeHtml(orderNo)}</div>
                </div>
              </div>
              <div class="tcl-bottom">
                <div class="tcl-factory">${escapeHtml(item.factoryName || "")}</div>
                <div class="tcl-location">
                  <span class="tcl-location-label">库位信息：</span>${escapeHtml(item.palletInfo || "")}
                </div>
              </div>
            </div>
          </div>
        `;
      });

      html += `
          </div>
          <script>
            var printIds = ${JSON.stringify(ids)};
            async function handlePrintClick() {
              if (confirm("确定要打印标签吗？")) {
                if (window.opener && window.opener.__batchUpdateIsPrint) {
                  try {
                    await window.opener.__batchUpdateIsPrint(printIds);
                  } catch (err) {
                    console.error("批量修改打印状态失败:", err);
                  }
                }
                window.print();
              }
            }
          </` + `script>
        </body>
        </html>
      `;

      printWindow.document.write(html);
      printWindow.document.close();
      printWindow.focus();
    };

    // A4批量打印：样式与表格汇总一致，按供应商分页区分（不影响 TCL / PLS / 非PLS 原逻辑）
    const processA4SummaryData = (data, materialAsc = false) => {
      const supplierMap = {};
      let totalMaterialCount = 0;

      const formatDemandNum = (num) => {
        const n = parseFloat(num);
        if (Number.isInteger(n)) return n.toString();
        return parseFloat(n.toFixed(10)).toString();
      };

      const extractInvNumber = (invCode) => {
        const match = String(invCode).match(/[A-Za-z]*([0-9]+)$/);
        return match ? parseInt(match[1], 10) : 0;
      };

      (data || []).forEach((wareOutItem) => {
        const supplierName = wareOutItem.supplierName || "未知供应商";
        const manufacturerName = wareOutItem.factoryName || "未知主机厂";
        const invCode = wareOutItem.invCode || "未知子库";
        if (!supplierMap[supplierName]) {
          supplierMap[supplierName] = {
            invMap: {},
            totalQty: 0,
            materialCount: 0,
            manufacturerName,
          };
        }
        if (!supplierMap[supplierName].invMap[invCode]) {
          supplierMap[supplierName].invMap[invCode] = {
            materialMap: {},
            totalQty: 0,
            materialCount: 0,
          };
        }

        const details =
          wareOutItem.wareOutDetailList &&
          wareOutItem.wareOutDetailList.length > 0
            ? wareOutItem.wareOutDetailList
            : [wareOutItem];

        details.forEach((detail) => {
          const materialNo = detail.materialNo || "未知编码";
          const materialDesc =
            (detail.materialDesc || "").substring(0, 50) +
            ((detail.materialDesc || "").length > 50 ? "..." : "");
          const demandNum =
            parseFloat(detail.demandNum) ||
            parseFloat(detail.wareOutNum) ||
            parseFloat(detail.prepareNum) ||
            0;

          if (
            !supplierMap[supplierName].invMap[invCode].materialMap[materialNo]
          ) {
            supplierMap[supplierName].invMap[invCode].materialMap[materialNo] =
              {
                materialNo,
                materialDesc,
                demandNum: 0,
                stockNum: null,
                packageNum: parseFloat(detail.packageNum) || 0,
                minPackageNum: parseFloat(detail.minPackageNumStr) || 0,
                palletInfo: {},
              };
            supplierMap[supplierName].materialCount++;
            supplierMap[supplierName].invMap[invCode].materialCount++;
            totalMaterialCount++;
          }

          const materialData =
            supplierMap[supplierName].invMap[invCode].materialMap[materialNo];
          materialData.demandNum += demandNum;
          supplierMap[supplierName].invMap[invCode].totalQty += demandNum;
          supplierMap[supplierName].totalQty += demandNum;

          // 当前库存取接口 stockNum，同物料取首次有效值
          if (
            materialData.stockNum == null &&
            detail.stockNum != null &&
            detail.stockNum !== ""
          ) {
            const sn = parseFloat(detail.stockNum);
            if (!Number.isNaN(sn)) materialData.stockNum = sn;
          }

          if (detail.recordList && detail.recordList.length > 0) {
            detail.recordList.forEach((record) => {
              const palletCode = record.palletCode || "未知";
              const qty = parseFloat(record.wareOutNum) || 0;
              if (!materialData.palletInfo[palletCode]) {
                materialData.palletInfo[palletCode] = 0;
              }
              materialData.palletInfo[palletCode] += qty;
            });
          }
        });
      });

      const result = [];
      let serialNumber = 1;
      Object.keys(supplierMap).forEach((supplierName) => {
        const supplierData = supplierMap[supplierName];
        const invCodes = Object.keys(supplierData.invMap);
        let isFirstInv = true;
        invCodes.forEach((invCode) => {
          const invData = supplierData.invMap[invCode];
          const materialNos = Object.keys(invData.materialMap);
          materialNos.forEach((materialNo, matIndex) => {
            const materialData = invData.materialMap[materialNo];
            const demandQty = materialData.demandNum;
            const cartonPack = formatPackagePack(
              materialData.packageNum,
              demandQty,
              "箱",
            );
            const gluePack = formatPackagePack(
              materialData.minPackageNum,
              demandQty,
              "胶箱",
            );
            const palletList = [];
            Object.keys(materialData.palletInfo || {}).forEach((palletCode) => {
              palletList.push(
                `${palletCode}：${materialData.palletInfo[palletCode].toFixed(0)}`,
              );
            });
            result.push({
              serialNumber: serialNumber++,
              supplierName: isFirstInv && matIndex === 0 ? supplierName : "",
              _supplierName: supplierName,
              manufacturerName:
                isFirstInv && matIndex === 0
                  ? supplierData.manufacturerName
                  : "",
              _manufacturerName: supplierData.manufacturerName,
              invCode,
              materialNo,
              materialDesc: materialData.materialDesc,
              demandNum: formatDemandNum(demandQty),
              stockNum:
                materialData.stockNum == null
                  ? ""
                  : formatDemandNum(materialData.stockNum),
              cartonPack,
              gluePack,
              palletInfo: palletList.join("; ") || "",
              isGroupStart: matIndex === 0,
            });
          });
          result.push({
            serialNumber: "",
            supplierName: "",
            _supplierName: supplierName,
            manufacturerName: "",
            _manufacturerName: supplierData.manufacturerName,
            invCode,
            materialNo: "",
            materialDesc: `${invCode} 计数`,
            demandNum: "",
            stockNum: "",
            cartonPack: "",
            gluePack: "",
            palletInfo: "",
            isInvTotal: true,
          });
          isFirstInv = false;
        });
        result.push({
          serialNumber: "",
          supplierName: "",
          _supplierName: supplierName,
          manufacturerName: "",
          _manufacturerName: supplierData.manufacturerName,
          invCode: "",
          materialNo: "",
          materialDesc: `${supplierName} 总计`,
          demandNum: supplierData.materialCount,
          stockNum: "",
          cartonPack: "",
          gluePack: "",
          palletInfo: "",
          isSupplierTotal: true,
        });
      });
      result.push({
        serialNumber: "",
        supplierName: "",
        _supplierName: "",
        manufacturerName: "",
        _manufacturerName: "",
        invCode: totalMaterialCount,
        materialNo: "",
        materialDesc: "总计数",
        demandNum: "",
        stockNum: "",
        cartonPack: "",
        gluePack: "",
        palletInfo: "",
        isGrandTotal: true,
      });

      result.sort((a, b) => {
        if (a.isGrandTotal) return 1;
        if (b.isGrandTotal) return -1;

        // 先按供应商分组，保证供应商总计紧跟在该供应商数据后面
        if (a._supplierName !== b._supplierName) {
          return String(a._supplierName || "").localeCompare(
            String(b._supplierName || ""),
            "zh",
          );
        }

        // 同一供应商内：供应商总计放最后
        if (a.isSupplierTotal && !b.isSupplierTotal) return 1;
        if (!a.isSupplierTotal && b.isSupplierTotal) return -1;

        const numA = extractInvNumber(a.invCode);
        const numB = extractInvNumber(b.invCode);
        if (numA !== numB) return numB - numA;
        const letterA = String(a.invCode || "").replace(/[0-9]+$/, "");
        const letterB = String(b.invCode || "").replace(/[0-9]+$/, "");
        if (letterA !== letterB) return letterB.localeCompare(letterA, "en");

        // 同一子库内：子库计数行放在普通数据行之后
        if (a.isInvTotal && !b.isInvTotal) return 1;
        if (!a.isInvTotal && b.isInvTotal) return -1;

        if (!a.isInvTotal && !b.isInvTotal) {
          const cmp = String(a.materialNo || "").localeCompare(
            String(b.materialNo || ""),
            "en",
          );
          if (cmp === 0) return 0;
          return materialAsc ? cmp : -cmp;
        }
        return 0;
      });

      let sn = 1;
      let lastInvCode = null;
      let lastSupplier = null;
      result.forEach((item) => {
        if (item.isInvTotal || item.isSupplierTotal || item.isGrandTotal) {
          item.serialNumber = "";
          return;
        }
        if (item._supplierName !== lastSupplier) {
          sn = 1;
          lastInvCode = null;
          lastSupplier = item._supplierName;
        }
        item.serialNumber = sn++;
        item.isGroupStart = item.invCode !== lastInvCode;
        lastInvCode = item.invCode;
      });

      return result;
    };

    const buildA4SummaryRowsHtml = (summaryData) => {
      let html = "";
      (summaryData || []).forEach((item) => {
        if (item.isGrandTotal) {
          html += `<tr class="total-row"><td colspan="3" style="text-align:left;padding-left:20px;">${item.materialDesc || ""}</td><td></td><td></td><td></td><td style="font-size:14px;">${item.invCode || ""}</td><td></td><td></td></tr>`;
        } else if (item.isSupplierTotal) {
          html += `<tr class="total-row"><td colspan="3" style="text-align:left;padding-left:40px;">${item.materialDesc || ""}</td><td>${item.demandNum || ""}</td><td></td><td></td><td></td><td></td><td></td></tr>`;
        } else if (item.isInvTotal) {
          html += `<tr class="total-row" style="background:#e6f7ff;"><td></td><td></td><td></td><td></td><td></td><td>${item.materialDesc || ""}</td><td>${item.invCode || ""}</td><td></td><td></td></tr>`;
        } else {
          const rowClass = item.isGroupStart ? "inv-group" : "material-row";
          html += `<tr class="${rowClass}"><td class="col-serial">${item.serialNumber || ""}</td><td class="col-supplier">${item._supplierName || item.supplierName || ""}</td><td class="col-material-no">${item.materialNo || ""}</td><td class="col-qty">${item.demandNum || ""}</td><td class="col-stock">${item.stockNum || ""}</td><td class="col-desc" style="text-align:left;">${item.materialDesc || ""}</td><td class="col-inv">${item.invCode || ""}</td><td class="col-carton">${item.cartonPack || ""}</td><td class="col-pallet">${item.palletInfo || ""}</td></tr>`;
        }
      });
      return html;
    };

    const openA4BatchSummaryPreview = (originalData) => {
      const allSummaryData = processA4SummaryData(originalData, false);

      const printWindow = window.open("", "_blank");
      const now = new Date();
      const printTime = `${now.getFullYear()}/${String(now.getMonth() + 1).padStart(2, "0")}/${String(now.getDate()).padStart(2, "0")} ${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;

      const styleCss = `
        * { box-sizing: border-box; margin: 0; padding: 0; }
        @media print {
          @page { size: A4 landscape; margin: 12mm; }
          body { font-family: 'Microsoft YaHei', Arial, sans-serif; font-size: 10px; padding: 0 !important; }
          .copy-toolbar { display: none !important; }
          .supplier-page { margin: 0 0 12px 0 !important; padding: 0 !important; page-break-inside: auto; break-inside: auto; }
          .print-header { page-break-after: avoid; break-after: avoid; }
          .summary-table { page-break-inside: auto; }
          .summary-table tr { page-break-inside: avoid; break-inside: avoid; }
        }
        body { font-family: 'Microsoft YaHei', Arial, sans-serif; font-size: 12px; padding: 20px; }
        .summary-container { max-width: 100%; margin: 0 auto; }
        .print-header { margin-bottom: 10px; padding-bottom: 8px; border-bottom: 2px solid #333; }
        .header-title { font-size: 20px; font-weight: bold; color: #333; margin-bottom: 8px; text-align: center; }
        .header-row { display: flex; justify-content: space-between; margin-bottom: 6px; font-size: 14px; }
        .header-left { flex: 1; text-align: left; font-weight: bold; }
        .header-right { flex: 1; text-align: right; color: #666; }
        .summary-table { width: 100%; border-collapse: collapse; margin-top: 8px; }
        .summary-table th, .summary-table td { border: 1px solid #333; padding: 6px 4px; text-align: center; vertical-align: middle; font-size: 11px; word-break: break-all; }
        .summary-table th { background: #f5f5f5; font-weight: bold; font-size: 12px; }
        .inv-group { background: #f0f8ff; }
        .total-row { background: #fff3cd; font-weight: bold; }
        .col-serial { width: 4%; }
        .col-supplier { width: 11%; }
        .col-material-no { width: 14%; }
        .col-qty { width: 7%; }
        .col-stock { width: 7%; }
        .col-desc { width: 16%; }
        .col-inv { width: 7%; }
        .col-carton { width: 13%; font-size: 13px; font-weight: bold; padding: 8px 4px; }
        .col-pallet { width: 17%; font-size: 12px; font-weight: bold; padding: 8px 4px; text-align: left; }
        .copy-toolbar { text-align: right; margin-bottom: 10px; }
        .copy-btn { padding: 6px 16px; border: 1px solid #1890ff; border-radius: 4px; background: white; color: #1890ff; font-size: 12px; cursor: pointer; margin-left: 8px; }
        .copy-btn:hover { background: #1890ff; color: white; }
        .copy-btn.primary { background: #1890ff; color: white; }
        .copy-btn.primary:hover { background: #096dd9; }
        .copy-btn:first-child { margin-left: 0; }
      `.replace(/\s+/g, " ").trim();

      const bodyHtml = `
        <div class="print-header">
          <div class="header-title">出库单汇总表</div>
          <div class="header-row">
            <div class="header-left"></div>
            <div class="header-right">打印时间：${printTime}</div>
          </div>
        </div>
        <div class="copy-toolbar">
          <button class="copy-btn primary" onclick="copySummaryToExcel()">复制到Excel</button>
          <button id="sortBtn" class="copy-btn" onclick="toggleMaterialSort()">编码从小到大</button>
        </div>
        <table class="summary-table">
          <thead>
            <tr>
              <th class="col-serial">序号</th>
              <th class="col-supplier">供应商</th>
              <th class="col-material-no">物料编码</th>
              <th class="col-qty">汇总数量</th>
              <th class="col-stock">当前库存</th>
              <th class="col-desc">物料描述</th>
              <th class="col-inv">子库</th>
              <th class="col-carton">纸箱包装</th>
              <th class="col-pallet">库位信息</th>
            </tr>
          </thead>
          <tbody>
            ${buildA4SummaryRowsHtml(allSummaryData.filter((item) => !item.isGrandTotal))}
          </tbody>
        </table>
      `;

      const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>A4批量打印预览</title><style>${styleCss}</style></head><body><div class="summary-container">${bodyHtml}</div></body></html>`;

      printWindow.document.write(html);
      printWindow.document.close();
      printWindow.focus();

      // 供排序使用
      printWindow.__a4OriginalData = originalData;
      printWindow.__a4MaterialAsc = false;
      printWindow.__a4ProcessSummaryData = processA4SummaryData;
      printWindow.__a4BuildRows = buildA4SummaryRowsHtml;

      const script = printWindow.document.createElement("script");
      script.textContent = `
        function toggleMaterialSort() {
          if (!window.__a4ProcessSummaryData || !window.__a4OriginalData || !window.__a4BuildRows) return;
          window.__a4MaterialAsc = !window.__a4MaterialAsc;
          var allData = window.__a4ProcessSummaryData(window.__a4OriginalData, window.__a4MaterialAsc);
          var rows = allData.filter(function(item) { return !item.isGrandTotal; });
          var tbody = document.querySelector('.summary-table tbody');
          if (tbody) tbody.innerHTML = window.__a4BuildRows(rows);
          var btn = document.getElementById('sortBtn');
          if (btn) btn.textContent = window.__a4MaterialAsc ? '编码从大到小' : '编码从小到大';
        }
        function copySummaryToExcel() {
          var L = [], T = String.fromCharCode(9), N = String.fromCharCode(10);
          var hr = document.querySelector('.header-row .header-right');
          var hVals = ['','','','','','','','',''];
          if (hr) {
            var kv2 = hr.textContent.trim().split('：');
            hVals[7] = kv2[kv2.length - 1].trim();
          }
          L.push(hVals.join(T));
          var th = document.querySelector('.summary-table thead');
          if (th) {
            var hc = th.querySelectorAll('tr th'), h = [];
            hc.forEach(function(c) { h.push(c.textContent.trim()); });
            L.push(h.join(T));
          }
          var tb = document.querySelector('.summary-table tbody');
          if (tb) {
            var rw = tb.querySelectorAll('tr');
            rw.forEach(function(r) {
              var cl = r.querySelectorAll('td'), rd = [];
              cl.forEach(function(c) { rd.push(c.textContent.trim()); });
              L.push(rd.join(T));
            });
          }
          var tx = L.join(N);
          var btn = document.querySelector('.copy-btn.primary') || document.querySelector('.copy-btn');
          if (btn) btn.textContent = '✓';
          var done = function() { if (btn) setTimeout(function() { btn.textContent = '复制到Excel'; }, 1500); };
          if (!navigator.clipboard) {
            var ta = document.createElement('textarea');
            ta.value = tx;
            document.body.appendChild(ta);
            ta.select();
            document.execCommand('copy');
            document.body.removeChild(ta);
            done();
            return;
          }
          navigator.clipboard.writeText(tx).then(done).catch(function() {
            var ta = document.createElement('textarea');
            ta.value = tx;
            document.body.appendChild(ta);
            ta.select();
            document.execCommand('copy');
            document.body.removeChild(ta);
            done();
          });
        }
      `;
      printWindow.document.body.appendChild(script);
      setTimeout(() => {
        printWindow.print();
      }, 250);
    };

    const batchPrintA4 = () => {
      if (!selectedData.value || selectedData.value.length === 0) {
        toast.error("请选择需要打印的出库单");
        return;
      }

      const container = document.createElement("div");
      container.style.cssText = `
        position: fixed; top: 0; left: 0; right: 0; bottom: 0;
        background: rgba(0,0,0,0.5); display: flex; align-items: center;
        justify-content: center; z-index: 9999;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
      `;
      const modal = document.createElement("div");
      modal.style.cssText = `
        background: white; border-radius: 12px; padding: 28px; width: 360px;
        box-shadow: 0 10px 40px rgba(0,0,0,0.15);
      `;
      const title = document.createElement("h3");
      title.textContent = "A4批量打印";
      title.style.cssText =
        "margin:0 0 16px 0;font-size:18px;font-weight:600;text-align:center;color:#1f2937;";
      modal.appendChild(title);

      const tip = document.createElement("p");
      tip.textContent = `确定要按供应商汇总打印选中的 ${selectedData.value.length} 条出库单吗？`;
      tip.style.cssText =
        "margin:0 0 20px 0;font-size:14px;color:#6b7280;text-align:center;line-height:1.5;";
      modal.appendChild(tip);

      const buttonRow = document.createElement("div");
      buttonRow.style.cssText = "display:flex;gap:12px;";

      const cancelBtn = document.createElement("button");
      cancelBtn.textContent = "取消";
      cancelBtn.style.cssText =
        "flex:1;padding:12px;border:none;border-radius:8px;background:#f3f4f6;font-size:14px;cursor:pointer;color:#6b7280;";
      cancelBtn.onclick = () => document.body.removeChild(container);
      buttonRow.appendChild(cancelBtn);

      const confirmBtn = document.createElement("button");
      confirmBtn.textContent = "确认打印";
      confirmBtn.style.cssText =
        "flex:1;padding:12px;border:none;border-radius:8px;background:linear-gradient(135deg,#1890ff 0%,#096dd9 100%);color:white;font-size:14px;font-weight:600;cursor:pointer;";
      confirmBtn.onclick = async () => {
        document.body.removeChild(container);
        const ids = selectedData.value.map((item) => toIdStr(item.id));
        try {
          await batchUpdateIsPrint(ids);
          const originalData = await batchGetPrintData(ids);
          if (originalData && originalData.length > 0) {
            openA4BatchSummaryPreview(originalData);
            const selectedIds = [...ids];
            await fetchPage();
            setTimeout(() => {
              selectedData.value = [];
              if (tableRef.value) {
                tableRef.value.clearSelection();
                list.value.forEach((row) => {
                  if (selectedIds.includes(row.id)) {
                    tableRef.value.toggleRowSelection(row, true);
                    selectedData.value.push(row);
                  }
                });
              }
            }, 100);
          } else {
            toast.error("获取打印数据失败或无数据可打印");
          }
        } catch (err) {
          console.error("A4批量打印异常:", err);
          toast.error("A4批量打印失败");
        }
      };
      buttonRow.appendChild(confirmBtn);
      modal.appendChild(buttonRow);
      container.appendChild(modal);
      document.body.appendChild(container);
    };

    const batchPrintTclMonthly = () => {
      if (!selectedData.value || selectedData.value.length === 0) {
        toast.error("请选择需要打印的出库单");
        return;
      }

      const container = document.createElement("div");
      container.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0,0,0,0.5);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 9999;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
      `;

      const modal = document.createElement("div");
      modal.style.cssText = `
        background: white;
        border-radius: 12px;
        padding: 28px;
        width: 360px;
        box-shadow: 0 10px 40px rgba(0,0,0,0.15);
      `;

      const title = document.createElement("h3");
      title.textContent = "TCL月度标签打印";
      title.style.cssText = `
        margin: 0 0 16px 0;
        font-size: 18px;
        font-weight: 600;
        text-align: center;
        color: #1f2937;
      `;
      modal.appendChild(title);

      const tip = document.createElement("p");
      tip.textContent = `确定要打印选中的 ${selectedData.value.length} 条出库单吗？`;
      tip.style.cssText = `
        margin: 0 0 20px 0;
        font-size: 14px;
        color: #6b7280;
        text-align: center;
        line-height: 1.5;
      `;
      modal.appendChild(tip);

      const buttonRow = document.createElement("div");
      buttonRow.style.cssText = "display: flex; gap: 12px;";

      const cancelBtn = document.createElement("button");
      cancelBtn.textContent = "取消";
      cancelBtn.style.cssText = `
        flex: 1;
        padding: 12px;
        border: none;
        border-radius: 8px;
        background: #f3f4f6;
        font-size: 14px;
        cursor: pointer;
        color: #6b7280;
      `;
      cancelBtn.onclick = () => {
        document.body.removeChild(container);
      };
      buttonRow.appendChild(cancelBtn);

      const confirmBtn = document.createElement("button");
      confirmBtn.textContent = "确认打印";
      confirmBtn.style.cssText = `
        flex: 1;
        padding: 12px;
        border: none;
        border-radius: 8px;
        background: linear-gradient(135deg, #e6a23c 0%, #cf9236 100%);
        color: white;
        font-size: 14px;
        font-weight: 600;
        cursor: pointer;
      `;
      confirmBtn.onclick = async () => {
        document.body.removeChild(container);
        const ids = selectedData.value.map((item) => toIdStr(item.id));
        try {
          const originalData = await batchGetPrintData(ids);
          if (originalData && originalData.length > 0) {
            const printData = processTclMonthlyPrintData(originalData);
            openTclMonthlyPrintPreview(printData, ids);
            const selectedIds = [...ids];
            await fetchPage();
            setTimeout(() => {
              selectedData.value = [];
              if (tableRef.value) {
                tableRef.value.clearSelection();
                list.value.forEach((row) => {
                  if (selectedIds.includes(row.id)) {
                    tableRef.value.toggleRowSelection(row, true);
                    selectedData.value.push(row);
                  }
                });
              }
            }, 100);
          } else {
            toast.error("获取打印数据失败或无数据可打印");
          }
        } catch (err) {
          console.error("TCL月度标签打印异常:", err);
          toast.error("TCL月度标签打印失败");
        }
      };
      buttonRow.appendChild(confirmBtn);
      modal.appendChild(buttonRow);

      container.appendChild(modal);
      document.body.appendChild(container);
    };

    const batchPrintNonPls = () => {
      if (!selectedData.value || selectedData.value.length === 0) {
        toast.error("请选择需要打印的出库单");
        return;
      }

      const invCodes = [
        ...new Set(
          selectedData.value.map((item) => item.invCode).filter(Boolean),
        ),
      ];

      const paperSizes = [
        { width: 100, height: 90, label: "100mm × 90mm" },
        { width: 100, height: 80, label: "100mm × 80mm" },
        { width: 100, height: 75, label: "100mm × 75mm" },
        { width: 100, height: 70, label: "100mm × 70mm" },
      ];

      const selectedInvCodes = [];

      const selectPaperSize = () => {
        const container = document.createElement("div");
        container.style.cssText = `
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0,0,0,0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 9999;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
        `;

        const modal = document.createElement("div");
        modal.style.cssText = `
          background: white;
          border-radius: 12px;
          padding: 28px;
          width: 360px;
          box-shadow: 0 10px 40px rgba(0,0,0,0.15);
        `;

        const title = document.createElement("h3");
        title.textContent = "选择纸张大小";
        title.style.cssText = `
          margin: 0 0 20px 0;
          font-size: 18px;
          font-weight: 600;
          text-align: center;
          color: #1f2937;
        `;
        modal.appendChild(title);

        const customLabel = document.createElement("div");
        customLabel.style.cssText = `
          margin-bottom: 20px;
        `;

        const labelText = document.createElement("label");
        labelText.textContent = "备注信息";
        labelText.style.cssText = `
          display: block;
          font-size: 14px;
          color: #4b5563;
          margin-bottom: 8px;
          font-weight: 500;
        `;
        customLabel.appendChild(labelText);

        const input = document.createElement("input");
        input.type = "text";
        input.placeholder = "输入备注信息";
        input.style.cssText = `
          width: 100%;
          padding: 10px 14px;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          font-size: 14px;
          box-sizing: border-box;
          transition: border-color 0.2s, box-shadow 0.2s;
        `;
        input.onfocus = () => {
          input.style.borderColor = "#3b82f6";
          input.style.boxShadow = "0 0 0 3px rgba(59, 130, 246, 0.1)";
        };
        input.onblur = () => {
          input.style.borderColor = "#d1d5db";
          input.style.boxShadow = "none";
        };
        customLabel.appendChild(input);
        modal.appendChild(customLabel);

        // 备料标签：默认显示物料描述；勾选后仅显示物料编码、不打印物料描述
        const onlyMaterialNoOption = document.createElement("label");
        onlyMaterialNoOption.style.cssText = `
          display: flex;
          align-items: center;
          cursor: pointer;
          margin-bottom: 20px;
          padding: 10px 12px;
          background: #f9fafb;
          border-radius: 8px;
          border: 1px solid #e5e7eb;
        `;
        const onlyMaterialNoCheckbox = document.createElement("input");
        onlyMaterialNoCheckbox.type = "checkbox";
        onlyMaterialNoCheckbox.checked = false;
        onlyMaterialNoCheckbox.style.cssText = `
          width: 16px;
          height: 16px;
          margin-right: 10px;
          cursor: pointer;
          flex-shrink: 0;
        `;
        const onlyMaterialNoText = document.createElement("span");
        onlyMaterialNoText.textContent =
          "物料描述仅显示物料编码";
        onlyMaterialNoText.style.cssText = `
          font-size: 13px;
          color: #374151;
          line-height: 1.4;
        `;
        onlyMaterialNoOption.appendChild(onlyMaterialNoCheckbox);
        onlyMaterialNoOption.appendChild(onlyMaterialNoText);
        modal.appendChild(onlyMaterialNoOption);

        if (invCodes.length > 0) {
          const invSection = document.createElement("div");
          invSection.style.cssText = `
            margin-bottom: 20px;
            padding-top: 16px;
            border-top: 1px solid #e5e7eb;
          `;

          const invTitle = document.createElement("label");
          invTitle.textContent = "子库拆包（选填）";
          invTitle.style.cssText = `
            display: block;
            font-size: 14px;
            color: #4b5563;
            margin-bottom: 12px;
            font-weight: 500;
          `;
          invSection.appendChild(invTitle);

          const invButtons = document.createElement("div");
          invButtons.style.cssText = `
            display: flex;
            gap: 10px;
            margin-bottom: 12px;
          `;

          const selectAllBtn = document.createElement("button");
          selectAllBtn.textContent = "全选";
          selectAllBtn.style.cssText = `
            padding: 6px 12px;
            border: 1px solid #3b82f6;
            border-radius: 4px;
            background: white;
            font-size: 12px;
            cursor: pointer;
            color: #3b82f6;
            transition: all 0.2s;
          `;
          selectAllBtn.onclick = () => {
            const checkboxes = invSection.querySelectorAll(
              'input[type="checkbox"]',
            );
            checkboxes.forEach((cb) => {
              cb.checked = true;
            });
            selectedInvCodes.length = 0;
            invCodes.forEach((code) => selectedInvCodes.push(code));
          };
          invButtons.appendChild(selectAllBtn);

          const clearAllBtn = document.createElement("button");
          clearAllBtn.textContent = "清空";
          clearAllBtn.style.cssText = `
            padding: 6px 12px;
            border: 1px solid #d1d5db;
            border-radius: 4px;
            background: white;
            font-size: 12px;
            cursor: pointer;
            color: #6b7280;
            transition: all 0.2s;
          `;
          clearAllBtn.onclick = () => {
            const checkboxes = invSection.querySelectorAll(
              'input[type="checkbox"]',
            );
            checkboxes.forEach((cb) => {
              cb.checked = false;
            });
            selectedInvCodes.length = 0;
          };
          invButtons.appendChild(clearAllBtn);
          invSection.appendChild(invButtons);

          const invList = document.createElement("div");
          invList.style.cssText = `
            display: flex;
            flex-wrap: wrap;
            gap: 10px;
          `;

          invCodes.forEach((invCode) => {
            const item = document.createElement("label");
            item.style.cssText = `
              display: flex;
              align-items: center;
              cursor: pointer;
              padding: 8px 12px;
              background: #f9fafb;
              border-radius: 6px;
              transition: background 0.2s;
            `;
            item.onmouseenter = () => {
              item.style.background = "#f3f4f6";
            };
            item.onmouseleave = () => {
              item.style.background = "#f9fafb";
            };

            const checkbox = document.createElement("input");
            checkbox.type = "checkbox";
            checkbox.value = invCode;
            checkbox.style.cssText = `
              width: 16px;
              height: 16px;
              margin-right: 8px;
              cursor: pointer;
            `;
            checkbox.onchange = (e) => {
              const idx = selectedInvCodes.indexOf(invCode);
              if (e.target.checked && idx === -1) {
                selectedInvCodes.push(invCode);
              } else if (!e.target.checked && idx > -1) {
                selectedInvCodes.splice(idx, 1);
              }
            };

            const text = document.createElement("span");
            text.textContent = invCode;
            text.style.cssText = `
              font-size: 13px;
              color: #374151;
            `;

            item.appendChild(checkbox);
            item.appendChild(text);
            invList.appendChild(item);
          });

          invSection.appendChild(invList);
          modal.appendChild(invSection);
        }

        const buttonGroup = document.createElement("div");
        buttonGroup.style.cssText = `
          display: flex;
          flex-direction: column;
          gap: 10px;
        `;

        paperSizes.forEach((size) => {
          const button = document.createElement("button");
          button.textContent = size.label;
          button.style.cssText = `
            padding: 14px;
            border: 1px solid #e5e7eb;
            border-radius: 8px;
            background: white;
            font-size: 15px;
            cursor: pointer;
            transition: all 0.2s;
            color: #374151;
            font-weight: 500;
          `;
          button.onmouseenter = () => {
            button.style.background = "#f9fafb";
            button.style.borderColor = "#3b82f6";
          };
          button.onmouseleave = () => {
            button.style.background = "white";
            button.style.borderColor = "#e5e7eb";
          };
          button.onclick = () => {
            document.body.removeChild(container);
            handleNonPlsPrintConfirm({
              ...size,
              customText: input.value.trim(),
              splitInvCodes: selectedInvCodes,
              onlyMaterialNo: onlyMaterialNoCheckbox.checked,
            });
          };
          buttonGroup.appendChild(button);
        });

        modal.appendChild(buttonGroup);

        const cancelBtn = document.createElement("button");
        cancelBtn.textContent = "取消";
        cancelBtn.style.cssText = `
          width: 100%;
          padding: 12px;
          border: none;
          border-radius: 8px;
          background: #f3f4f6;
          font-size: 14px;
          cursor: pointer;
          margin-top: 12px;
          color: #6b7280;
          transition: background 0.2s;
        `;
        cancelBtn.onmouseenter = () => {
          cancelBtn.style.background = "#e5e7eb";
        };
        cancelBtn.onmouseleave = () => {
          cancelBtn.style.background = "#f3f4f6";
        };
        cancelBtn.onclick = () => {
          document.body.removeChild(container);
        };
        modal.appendChild(cancelBtn);

        container.appendChild(modal);
        document.body.appendChild(container);
      };

      selectPaperSize();
    };

    const handleNonPlsPrintConfirm = (paperSize) => {
      const confirmContainer = document.createElement("div");
      confirmContainer.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0,0,0,0.5);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 9999;
      `;

      const confirmModal = document.createElement("div");
      confirmModal.style.cssText = `
        background: white;
        border-radius: 8px;
        padding: 24px;
        width: 320px;
        box-shadow: 0 4px 20px rgba(0,0,0,0.2);
      `;

      const confirmTitle = document.createElement("h3");
      confirmTitle.textContent = "确认打印";
      confirmTitle.style.cssText = `
        margin: 0 0 16px 0;
        font-size: 16px;
        font-weight: bold;
        text-align: center;
      `;
      confirmModal.appendChild(confirmTitle);

      const confirmText = document.createElement("p");
      confirmText.textContent = `确定要打印选中的 ${selectedData.value.length} 条出库单吗？`;
      confirmText.style.cssText = `
        margin: 0 0 20px 0;
        font-size: 14px;
        color: #666;
        text-align: center;
        line-height: 1.5;
      `;
      confirmModal.appendChild(confirmText);

      const buttonContainer = document.createElement("div");
      buttonContainer.style.cssText = `
        display: flex;
        gap: 12px;
      `;

      const cancelBtn = document.createElement("button");
      cancelBtn.textContent = "取消";
      cancelBtn.style.cssText = `
        flex: 1;
        padding: 10px;
        border: 1px solid #ddd;
        border-radius: 4px;
        background: white;
        font-size: 14px;
        cursor: pointer;
        transition: all 0.2s;
      `;
      cancelBtn.onmouseenter = () => {
        cancelBtn.style.background = "#f5f5f5";
      };
      cancelBtn.onmouseleave = () => {
        cancelBtn.style.background = "white";
      };
      cancelBtn.onclick = () => {
        document.body.removeChild(confirmContainer);
      };
      buttonContainer.appendChild(cancelBtn);

      const confirmBtn = document.createElement("button");
      confirmBtn.textContent = "确定";
      confirmBtn.style.cssText = `
        flex: 1;
        padding: 10px;
        border: none;
        border-radius: 4px;
        background: linear-gradient(135deg, #1890ff 0%, #096dd9 100%);
        color: white;
        font-size: 14px;
        cursor: pointer;
        transition: all 0.2s;
      `;
      confirmBtn.onmouseenter = () => {
        confirmBtn.style.transform = "translateY(-2px)";
        confirmBtn.style.boxShadow = "0 4px 12px rgba(24, 144, 255, 0.4)";
      };
      confirmBtn.onmouseleave = () => {
        confirmBtn.style.transform = "translateY(0)";
        confirmBtn.style.boxShadow = "none";
      };
      confirmBtn.onclick = async () => {
        document.body.removeChild(confirmContainer);
        const ids = selectedData.value.map((item) => toIdStr(item.id));

        try {
          await batchUpdateIsPrint(ids);
          const originalData = await batchGetPrintData(ids);
          if (originalData && originalData.length > 0) {
            const printData = processNonPlsPrintData(originalData);
            openNonPlsPrintPreview(printData, paperSize, originalData);
            const selectedIds = [...ids];
            await fetchPage();
            setTimeout(() => {
              selectedData.value = [];
              if (tableRef.value) {
                tableRef.value.clearSelection();
                list.value.forEach((row) => {
                  if (selectedIds.includes(row.id)) {
                    tableRef.value.toggleRowSelection(row, true);
                    selectedData.value.push(row);
                  }
                });
              }
            }, 100);
          } else {
            toast.error("获取打印数据失败或无数据可打印");
          }
        } catch (err) {
          console.error("批量打印异常:", err);
          toast.error("批量打印失败");
        }
      };
      buttonContainer.appendChild(confirmBtn);

      confirmModal.appendChild(buttonContainer);
      confirmContainer.appendChild(confirmModal);
      document.body.appendChild(confirmContainer);
    };

    const openNonPlsPrintPreview = (printData, paperSize, originalData) => {
      const width = paperSize?.width || 100;
      const height = paperSize?.height || 90;
      const customText = paperSize?.customText || "";
      const onlyMaterialNo = !!paperSize?.onlyMaterialNo;
      const printWindow = window.open("", "_blank");
      const now = new Date();
      const printTime = `${(now.getMonth() + 1)
        .toString()
        .padStart(2, "0")}/${now.getDate().toString().padStart(2, "0")} ${now
        .getHours()
        .toString()
        .padStart(2, "0")}:${now.getMinutes().toString().padStart(2, "0")}`;

      let summaryHtml = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8">
          <title>打印预览</title>
          <style>
            * {
              box-sizing: border-box;
              margin: 0;
              padding: 0;
            }
            @media print {
              @page {
                size: ${width}mm ${height}mm;
                margin: 0;
                padding: 0;
              }
              body {
                margin: 0;
                padding: 0;
                font-family: "Microsoft YaHei", Arial, sans-serif;
                font-size: 14px;
              }
              .label-card {
                page-break-after: always;
                break-after: page;
                overflow: hidden;
                padding: 2mm 5mm;
                margin: 0 !important;
                width: ${width}mm !important;
                height: ${height}mm !important;
              }
            }
            html, body {
              margin: 0;
              padding: 0;
              font-family: "Microsoft YaHei", Arial, sans-serif;
              background: transparent;
            }
            body {
              width: ${width}mm;
              height: ${height}mm;
            }
            .label-card {
              width: ${width}mm;
              height: ${height}mm;
              margin: 0;
              padding: 0;
              background: transparent;
              box-shadow: none;
              border: none;
              box-sizing: border-box;
              page-break-after: always;
            }
            .label-table {
              width: 100%;
              height: 100%;
              margin: 0;
              padding: 0;
              border-collapse: collapse;
              border: 2px solid #000;
              table-layout: fixed;
            }
            .label-table td {
              border: 1px solid #000;
              padding: ${height === 70 ? "1px 2px" : "2px 4px"};
              vertical-align: middle;
              text-align: center;
              font-size: ${
                height === 70 ? "9px" : height === 75 ? "10px" : "11px"
              };
              line-height: 1.1;
            }
            .label-table tr {
              height: ${height === 70 ? "12%" : height === 75 ? "11%" : "10%"};
            }

            .label-cell {
              font-size: ${
                height === 70 ? "18px" : height === 75 ? "20px" : "22px"
              }!important;
              text-align: left;
              padding-left: ${height === 70 ? "3px" : "5px"};
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
              width: 22%;
            }
            .supplier-cell {
              vertical-align: top;
              padding-top: 4px;
              font-size: ${
                height === 70 ? "18px" : height === 75 ? "20px" : "22px"
              } !important;
              font-weight: bold !important;
              white-space: normal;
              word-break: break-word;
              width: 50%;
              position: relative;
            }
            .qr-cell {
              vertical-align: middle;
              text-align: center;
              width: 90px;
              padding: 2px !important;
            }
            .qr-code-wrapper {
              display: flex;
              justify-content: center;
              align-items: center;
              width: 86px;
              height: 86px;
              margin: 0 auto;
              background: #fff;
            }
            .qr-code-wrapper img,
            .qr-code-wrapper canvas {
              width: 86px !important;
              height: 86px !important;
              image-rendering: pixelated;
            }
            .time-label-cell {
              font-size: ${
                height === 70 ? "12px" : height === 75 ? "14px" : "16px"
              } !important;
              width: 22%;
            }
            .time-value-cell {
              font-size: ${
                height === 70 ? "12px" : height === 75 ? "14px" : "16px"
              } !important;
              width: 28%;
            }
            .label-table tr:first-child {
              height: ${
                height === 70 ? "12%" : height === 75 ? "11%" : "10%"
              } !important;
            }
            .large-font-cell {
              font-size: ${
                height === 70 ? "22px" : height === 75 ? "24px" : "26px"
              } !important;
              font-weight: bold !important;
              padding-top: 0 !important;
              padding-bottom: 0 !important;
              line-height: 1 !important;
            }
            .pack-cell {
              font-size: ${
                height === 70 ? "18px" : height === 75 ? "20px" : "22px"
              } !important;
              padding-top: 0 !important;
              padding-bottom: 0 !important;
              font-weight: bold !important;
              line-height: 1 !important;
            }
            .value-cell {
              font-size: ${
                height === 70 ? "18px" : height === 75 ? "20px" : "22px"
              } !important;
              text-align: left;
              font-weight: bold !important;
            }
            .value-cell-width {
              width: 22%;
            }
            .value-bold {
              font-size: ${height === 75 ? "12px" : "14px"} !important;
              font-weight: bold !important;
            }
            .material-no-cell {
              font-size: ${height === 75 ? "22px" : "26px"} !important;
              font-weight: 800 !important;
              letter-spacing: 2px !important;
              text-align: center;
              padding-top: 0 !important;
              padding-bottom: 0 !important;
              line-height: 1 !important;
            }
            .desc-cell {
              font-size: ${height === 75 ? "16px" : "18px"} !important;
              font-weight: bold !important;
              white-space: normal;
              overflow: hidden;
              text-align: left;
              padding-top: 0 !important;
              padding-bottom: 0 !important;
              line-height: 1.3 !important;
              max-height: ${height === 75 ? "42px" : "47px"} !important;
            }
            .desc-cell.desc-code-only {
              font-size: ${height === 75 ? "22px" : "26px"} !important;
              font-weight: 800 !important;
              line-height: 1.2 !important;
              max-height: ${height === 75 ? "48px" : "56px"} !important;
            }
            .print-toolbar {
              position: fixed;
              top: 0;
              left: 0;
              right: 0;
              background: #333;
              padding: 10px 20px;
              display: flex;
              justify-content: flex-end;
              gap: 12px;
              z-index: 1000;
            }
            .toolbar-btn {
              padding: 8px 20px;
              border: none;
              border-radius: 4px;
              font-size: 14px;
              cursor: pointer;
              transition: all 0.2s;
            }
            .toolbar-btn.primary {
              background: #1890ff;
              color: white;
            }
            .toolbar-btn.primary:hover {
              background: #096dd9;
            }
            .toolbar-btn.secondary {
              background: #fff;
              color: #333;
            }
            .toolbar-btn.secondary:hover {
              background: #e6e6e6;
            }
            .toolbar-btn.tertiary {
              background: #fa8c16;
              color: white;
            }
            .toolbar-btn.tertiary:hover {
              background: #d46b08;
            }
            @media print {
              body {
                margin: 0 !important;
                padding: 0 !important;
              }
              html {
                margin: 0 !important;
                padding: 0 !important;
              }
              .print-content {
                margin-top: 0 !important;
              }
              .print-toolbar {
                display: none !important;
                position: absolute !important;
                top: -9999px !important;
                left: -9999px !important;
              }
              .print-content {
                margin-top: 0 !important;
                margin: 0 !important;
                padding: 0 !important;
                position: absolute !important;
                top: 0 !important;
                left: 0 !important;
              }
              .label-card {
                margin: 0 !important;
                padding: 2mm 3mm;
                overflow: hidden;
                page-break-after: always !important;
              }
            }
          </style>
        </head>
        <body style="margin: 0; padding: 0;">
          <div class="print-toolbar">
            <button class="toolbar-btn secondary" onclick="handlePrintClick()">打印标签</button>
            <button class="toolbar-btn primary" onclick="openSummaryPrint()">表格汇总</button>
          </div>
          <div class="print-content" style="margin-top: 50px;">
      `;

      printData.forEach((item, index) => {
        const labelNo = index + 1;
        const qrContent = encodeURIComponent(buildLabelQrContent(item));
        summaryHtml += `
          <div class="label-card">
            <table class="label-table">
              <tr>
                <td class="label-cell qr-cell" rowspan="2">
                  <div class="qr-code-wrapper">
                    <canvas class="qr-canvas" data-qr="${qrContent}"></canvas>
                  </div>
                </td>
                <td class="value-cell value-bold">NO${labelNo}</td>
                <td class="value-cell value-bold" colspan="2">${item.supplierName || ""}${customText ? customText : ""}</td>
              </tr>
              <tr>
                <td class="value-cell time-value-cell">${printTime}</td>
                <td class="label-cell">仓库</td>
                <td class="value-cell value-cell-width"><strong>${
                  item.invCode || ""
                }</strong></td>
              </tr>
              <tr>
                <td class="label-cell">物料编码</td>
                <td class="material-no-cell" colspan="3"${
                  item.isLongMaterialNo
                    ? ' style="font-size: 16px !important;"'
                    : ""
                }>${"\u200B" + (item.displayMaterialNo || "")}</td>
              </tr>
              <tr>
                <td class="label-cell">物料描述</td>
                <td class="desc-cell${
                  onlyMaterialNo ? " desc-code-only" : ""
                }" colspan="3">${
                  onlyMaterialNo
                    ? item.materialNo || ""
                    : item.displayMaterialDesc || ""
                }</td>
              </tr>
              <tr>
                <td class="label-cell">需求数量</td>
                <td class="value-cell large-font-cell"><strong>${
                  item.demandNum
                }</strong></td>
                <td class="label-cell">当前库存</td>
                <td class="value-cell value-cell-width"><strong>${
                  item.stockNum ?? ""
                }</strong></td>
              </tr>
              <tr>
                <td class="label-cell">纸箱包装</td>
                <td class="value-cell pack-cell" colspan="3"><strong>${
                  item.cartonPack || ""
                }</strong></td>
              </tr>
              <tr>
                <td class="label-cell">胶框包装</td>
                <td class="value-cell pack-cell" colspan="3"><strong>${
                  item.gluePack || ""
                }</strong></td>
              </tr>
              <tr>
                <td class="label-cell">库存信息</td>
                <td class="value-cell large-font-cell" colspan="3"><strong>${
                  item.palletInfo || ""
                }</strong></td>
              </tr>
            </table>
          </div>
        `;
      });

      summaryHtml += `
          </div>
        </body>
        </html>
      `;

      printWindow.document.write(summaryHtml);
      printWindow.document.close();

      setTimeout(async () => {
        try {
          await renderLabelQrCodes(printWindow);
        } catch (e) {
          console.error("二维码生成失败:", e);
        }
        setTimeout(() => {
          printWindow.print();
        }, 200);
      }, 100);

      const summaryData = processNonPlsSummaryData(originalData);
      const summaryJson = JSON.stringify(summaryData);

      const openSummaryFn = `
        var nonPlsSummaryData = ${summaryJson};
        var nonPlsPrintTime = "${printTime}";
        
        function openSummaryPrint() {
          var printWindow = window.open("", "_blank");
          
          var firstItem = nonPlsSummaryData.find(function(item) { return item.supplierName && item.supplierName !== ''; });
          var supplierName = firstItem ? firstItem.supplierName : '';
          var manufacturerName = firstItem ? firstItem.manufacturerName : '';
          
          var html = "<!DOCTYPE html><html><head><meta charset='UTF-8'><title>出库汇总打印预览</title><style>*{box-sizing:border-box;margin:0;padding:0;}@media print{@page{size:A4 landscape;margin:15mm;}body{font-family:'Microsoft YaHei',Arial,sans-serif;font-size:10px;}}body{font-family:'Microsoft YaHei',Arial,sans-serif;font-size:12px;padding:20px;}.summary-container{max-width:100%;margin:0 auto;}.print-header{margin-bottom:15px;padding-bottom:10px;border-bottom:2px solid #333;}.header-title{font-size:20px;font-weight:bold;color:#333;margin-bottom:10px;text-align:center;}.header-row{display:flex;justify-content:space-between;margin-bottom:8px;font-size:14px;}.header-left{flex:1;text-align:left;font-weight:bold;}.header-right{flex:1;text-align:right;color:#666;}.summary-table{width:100%;border-collapse:collapse;margin-top:10px;}.summary-table th,.summary-table td{border:1px solid #333;padding:6px 4px;text-align:center;vertical-align:middle;font-size:11px;word-break:break-all;}.summary-table th{background:#f5f5f5;font-weight:bold;font-size:12px;}.inv-group{background:#f0f8ff;}.total-row{background:#fff3cd;font-weight:bold;}.col-serial{width:6%;}.col-material-no{width:18%;}.col-qty{width:10%;}.col-desc{width:22%;}.col-inv{width:10%;}.col-carton{width:20%;font-size:13px;font-weight:bold;padding:8px 4px;}.col-glue{width:14%;font-size:13px;font-weight:bold;padding:8px 4px;}.print-footer{text-align:center;margin-top:20px;padding-top:10px;border-top:1px solid #ccc;font-size:10px;color:#999;}.copy-toolbar{text-align:right;margin-bottom:10px;}.copy-btn{padding:6px 16px;border:1px solid #1890ff;border-radius:4px;background:white;color:#1890ff;font-size:12px;cursor:pointer;}.copy-btn:hover{background:#1890ff;color:white;}</style></head><body><div class='summary-container'><div class='print-header'><div class='header-title'>出库单非PLS汇总表</div><div class='header-row'><div class='header-left'>供应商：" + supplierName + " | 主机厂：" + manufacturerName + "</div><div class='header-right'>打印时间：" + nonPlsPrintTime + "</div></div></div><div class='copy-toolbar'><button class='copy-btn' onclick='copySummaryToExcel()'>复制到Excel</button></div><table class='summary-table'><thead><tr><th class='col-serial'>序号</th><th class='col-material-no'>物料编码</th><th class='col-qty'>汇总数量</th><th class='col-desc'>物料描述</th><th class='col-inv'>子库</th><th class='col-carton'>纸箱包装</th><th class='col-glue'>胶框包装</th></tr></thead><tbody>";
          
          nonPlsSummaryData.forEach(function(item) {
            if (item.isSupplierTotal) {
              html += "<tr class='total-row'><td colspan='3' style='text-align:left;padding-left:40px;'>" + item.materialDesc + "</td><td>" + item.demandNum + "</td><td></td><td></td><td></td></tr>";
            } else if (item.isInvTotal) {
              html += "<tr class='total-row' style='background:#e6f7ff;'><td></td><td></td><td></td><td>" + item.materialDesc + "</td><td>" + item.invCode + "</td><td></td><td></td></tr>";
            } else {
              var rowClass = item.isGroupStart ? "inv-group" : "material-row";
              var materialNo = item.composeMaterialNo ? item.composeMaterialNo : item.materialNo;
              var materialDesc = item.composeMaterialNo ? (item.materialDesc + " (" + item.materialNo + ")") : item.materialDesc;
              html += "<tr class='" + rowClass + "'><td class='col-serial'>" + (item.serialNumber || "") + "</td><td class='col-material-no'>" + (materialNo || "") + "</td><td class='col-qty'>" + (item.demandNum || "") + "</td><td class='col-desc' style='text-align:left;'>" + (materialDesc || "") + "</td><td class='col-inv'>" + (item.invCode || "") + "</td><td class='col-carton'>" + (item.cartonPack || "") + "</td><td class='col-glue'>" + (item.gluePack || "") + "</td></tr>";
            }
          });
          
          html += "</tbody></table><div class='print-footer'>打印时间：" + nonPlsPrintTime + " | 出库单非PLS汇总表</div></div></body></html>";
          
          printWindow.document.write(html);
          printWindow.document.close();
          printWindow.focus();
          
          var copyFn = "function copySummaryToExcel(){var tb=document.querySelector('.summary-table tbody');if(!tb)return;var th=document.querySelector('.summary-table thead');var L=[],T=String.fromCharCode(9),N=String.fromCharCode(10);var hl=document.querySelector('.header-row .header-left');var hr=document.querySelector('.header-row .header-right');var hVals=['','','','','','','',''];if(hl){var hparts=hl.textContent.trim().split('|');var supplier='',manufacturer='';for(var hpi=0;hpi<hparts.length;hpi++){var kv=hparts[hpi].trim().split('：');if(hparts[hpi].trim().indexOf('供应商')!==-1){supplier=kv[kv.length-1].trim()}else if(hparts[hpi].trim().indexOf('主机厂')!==-1){manufacturer=kv[kv.length-1].trim()}}hVals[0]=manufacturer;hVals[1]=supplier}if(hr){var kv=hr.textContent.trim().split('：');hVals[6]=kv[kv.length-1].trim()}L.push(hVals.join(T));if(th){var hc=th.querySelectorAll('tr th'),h=[];hc.forEach(function(c){h.push(c.textContent.trim())});L.push(h.join(T))}var rw=tb.querySelectorAll('tr');rw.forEach(function(r){var cl=r.querySelectorAll('td'),rd=[];cl.forEach(function(c){rd.push(c.textContent.trim())});L.push(rd.join(T))});var tx=L.join(N);var btn=document.querySelector('.copy-btn');if(btn)btn.textContent='\u2713';if(!navigator.clipboard){var ta=document.createElement('textarea');ta.value=tx;document.body.appendChild(ta);ta.select();document.execCommand('copy');document.body.removeChild(ta);if(btn)setTimeout(function(){btn.textContent='\u590D\u5236\u5230Excel'},1500);return}navigator.clipboard.writeText(tx).then(function(){if(btn)setTimeout(function(){btn.textContent='\u590D\u5236\u5230Excel'},1500)})['catch'](function(){var ta=document.createElement('textarea');ta.value=tx;document.body.appendChild(ta);ta.select();document.execCommand('copy');document.body.removeChild(ta);if(btn)setTimeout(function(){btn.textContent='\u590D\u5236\u5230Excel'},1500)})}";
          var cs = printWindow.document.createElement("script");
          cs.textContent = copyFn;
          printWindow.document.body.appendChild(cs);
          
          setTimeout(function() { printWindow.print(); }, 250);
        }
      `;

      setTimeout(() => {
        const script = printWindow.document.createElement("script");
        script.textContent = openSummaryFn;
        printWindow.document.body.appendChild(script);
      }, 300);
    };

    const handleMessage = async (event) => {
      if (event.data && event.data.type === "confirmPlsPrint") {
        const ids = event.data.ids || [];
        if (ids.length > 0) {
          try {
            await batchUpdateIsPrint(ids);
          } catch (err) {
            console.error("批量修改打印状态失败:", err);
          }
        }
      }
    };

    onMounted(() => {
      if (route.query?.from === "home") {
        wareOutStatus.value = "3";
      }
      fetchPage();

      document.addEventListener("click", (e) => {
        if (
          !e.target.closest(".search-input-wrapper") &&
          !e.target.closest(".dropdown-menu")
        ) {
          supplierDropdown.value = false;
        }
      });

      window.addEventListener("message", handleMessage);
      window.__batchUpdateIsPrint = batchUpdateIsPrint;
    });

    onUnmounted(() => {
      window.removeEventListener("message", handleMessage);
      delete window.__batchUpdateIsPrint;
    });

    return {
      current,
      size,
      list,
      total,
      loading,
      supplierKeyword,
      supplierLoading,
      supplierDropdown,
      supplierList,
      selectedSupplier,
      resourceNoTags,
      invCodeTags,
      materialNoTags,
      timeRange,
      startTime,
      endTime,
      isPrint,
      wareOutStatus,
      wareOutType,
      selectedData,
      tableRef,
      pages,
      isAllSelected,
      isSelected,
      selectedTotalQty,
      getRowKey,
      calculateSpanMethod,
      handleBack,
      openDeliveryPlanImport,
      handleSupplierSearch,
      truncateText,
      formatNumber,
      handleCopy,
      selectSupplier,
      clearSelectedSupplier,
      handleSearch,
      handleTimeChange,
      onPrintChange,
      handleReset,
      handlePageChange,
      handleSizeChange,
      toggleSelectItem,
      toggleSelectAll,
      fetchSupplierSuggestions,
      getRowClassName,
      handleSelect,
      handleSelectionChange,
      handleDetail,
      getStatusText,
      getStatusTagType,
      detailVisible,
      detailLoading,
      detailData,
      previewPrint,
      batchPrint,
      batchPrintA4,
      batchPrintTclMonthly,
      batchPrintNonPls,
      previewNonPlsSummary,
    };
  },
};
</script>

<style scoped>
.page-container {
  min-height: 100vh;
  background: #f0f2f5;
}

.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 20px;
  background: #23b1c0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
}

.back-btn {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 6px;
  cursor: pointer;
}

.back-btn:hover {
  background: rgba(255, 255, 255, 0.3);
}

.icon-back {
  width: 18px;
  height: 18px;
  filter: brightness(0) invert(1);
}

.title {
  font-size: 18px;
  font-weight: 600;
  margin: 0;
  color: #fff;
}

.header-right {
  min-width: 36px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.link-btn {
  height: 32px;
  padding: 0 10px;
  font-size: 13px;
  color: #fff;
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.35);
  border-radius: 6px;
  cursor: pointer;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.link-btn-icon {
  font-size: 15px;
}

.link-btn:hover {
  background: rgba(255, 255, 255, 0.3);
}

.content {
  padding: 16px 24px;
  width: 100%;
  box-sizing: border-box;
}

.search-panel {
  background: #fff;
  padding: 16px 16px 4px;
  border-radius: 8px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  margin-bottom: 12px;
}

.filter-form {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  column-gap: 12px;
  width: 100%;
}

.filter-form :deep(.el-form-item) {
  margin-bottom: 12px;
  margin-right: 0;
}

.filter-form :deep(.el-form-item__label) {
  color: #606266;
  font-weight: 500;
}

/* 创建时间与查询/重置同一行，压缩搜索区高度 */
.form-item-date {
  grid-column: 1 / 4;
}

.form-item-actions {
  grid-column: 4 / 5;
  align-self: end;
}

.form-item-actions :deep(.el-form-item__content) {
  display: flex;
  justify-content: flex-end;
  flex-wrap: nowrap;
  gap: 8px;
  margin-left: 0 !important;
}

.filter-form :deep(.el-input-tag) {
  width: 100%;
}

.table-panel {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  padding: 12px 16px 8px;
}

.table-action-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.action-buttons {
  display: flex;
  gap: 8px;
}

.selected-info {
  font-size: 14px;
  color: #606266;
}

.selected-count {
  color: #409eff;
  font-weight: 600;
  margin: 0 4px;
}

.selected-qty {
  color: #909399;
}

.print-data-table {
  width: 100%;
}

.print-data-table :deep(.selected-row) {
  --el-table-tr-bg-color: #ecf5ff;
}

.copyable {
  cursor: copy;
  user-select: text;
}

.copyable:hover {
  color: #409eff;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 48px 0;
}

.empty-img {
  width: 88px;
  height: 88px;
  opacity: 0.4;
}

.empty-text {
  margin-top: 12px;
  color: #909399;
  font-size: 14px;
}

.pagination {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 12px 0 4px;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #f0f0f0;
  border-top: 3px solid #409eff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.loading-text {
  margin-top: 12px;
  color: #909399;
  font-size: 14px;
}

.detail-content {
  overflow-y: auto;
}

.detail-info-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 16px 24px;
  padding: 0.75rem 0;
  border-bottom: 1px solid #f0f0f0;
}

.info-item {
  display: flex;
  align-items: center;
}

.info-label {
  font-size: 0.875rem;
  color: #606266;
  margin-right: 0.5rem;
}

.info-value {
  font-size: 0.875rem;
  color: #303133;
}

.detail-section {
  margin-top: 1.5rem;
}

.detail-section-title {
  font-size: 0.9375rem;
  font-weight: 600;
  color: #303133;
  margin-bottom: 0.75rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid #4db6ac;
}

.ellipsis-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.detail-table-wrapper {
  overflow-x: auto;
  max-height: 400px;
  overflow-y: auto;
}

@media (max-width: 1400px) {
  .filter-form {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .form-item-date {
    grid-column: 1 / 3;
  }

  .form-item-actions {
    grid-column: 3 / 4;
  }
}

@media (max-width: 1100px) {
  .filter-form {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .form-item-date {
    grid-column: 1 / 2;
  }

  .form-item-actions {
    grid-column: 2 / 3;
  }
}
</style>
